import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowRight,
  Ban,
  BarChart3,
  Bell,
  Boxes,
  Building2,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Download,
  ExternalLink,
  FilePlus2,
  FileText,
  GripVertical,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  Copy,
  Database,
  History,
  Mail,
  MailCheck,
  Menu,
  PackagePlus,
  Pencil,
  Plus,
  Power,
  ReceiptText,
  Save,
  Search,
  Send,
  Settings,
  ShieldCheck,
  Stamp,
  Tags,
  Terminal,
  Trash2,
  Upload,
  UserPlus,
  Users,
  WalletCards,
  X,
} from "lucide-react";
import { Link, NavLink, Route, Routes, useLocation } from "react-router-dom";
import "./admin.css";

type RecordRow = Record<string, unknown>;

interface DashboardData {
  identity: { email: string };
  metrics: {
    outstandingCents: number;
    overdueCents: number;
    paidThisMonthCents: number;
    activeCustomers: number;
  };
  statusCounts: Record<string, number>;
  revenue: Array<{ month: string; invoicedCents: number; paidCents: number }>;
  recentInvoices: Array<{
    id: string;
    invoice_number: string;
    customer: string;
    issue_date: string;
    due_date: string;
    total_cents: number;
    status: string;
  }>;
}

const currency = (cents: number | unknown) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(cents || 0) / 100,
  );
const shortDate = (value: unknown) =>
  value
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(`${value}T12:00:00`))
    : "—";
const titleCase = (value: unknown) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

function suggestedManagementUrl(natIp: string, realIp: string) {
  const rawHost = (natIp || realIp).split(",")[0].trim().replace(/\/\d{1,3}$/, "");
  if (!rawHost) return "";
  const host = rawHost.includes(":") && !rawHost.startsWith("[") ? `[${rawHost}]` : rawHost;
  return `https://${host}`;
}

function monitoringIp(value: unknown) {
  return String(value || "").split(",")[0].trim().replace(/\/\d{1,3}$/, "");
}

async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`/api/admin/${path}`, {
    ...options,
    headers: options?.body
      ? { "Content-Type": "application/json", ...options.headers }
      : options?.headers,
  });
  const body = (await response.json().catch(() => ({}))) as {
    message?: string;
  };
  if (!response.ok)
    throw new Error(body.message || "The request could not be completed.");
  return body as T;
}

function useApi<T>(path: string, refresh = 0) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    if (!path) {
      setLoading(false);
      setData(null);
      setError("");
      return () => {
        active = false;
      };
    }
    setLoading(true);
    api<T>(path)
      .then((result) => {
        if (active) {
          setData(result);
          setError("");
        }
      })
      .catch((reason) => {
        if (active)
          setError(
            reason instanceof Error ? reason.message : "Unable to load data.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [path, refresh]);
  return { data, error, loading };
}

const invoiceDisplayStatus = (value: unknown) =>
  String(value) === "void" ? "canceled" : value;
const invoicePdfUrl = (id: unknown) =>
  `/api/admin/invoices/${encodeURIComponent(String(id))}/pdf`;

const navItems = [
  ["/admin", "Dashboard", LayoutDashboard, true],
  ["/admin/customers", "Customers", Users],
  ["/admin/invoices", "Invoices", FileText],
  ["/admin/contracted-services", "Contracted Services", Database],
  ["/admin/catalog", "SKU Catalog", Boxes],
  ["/admin/packages", "Packages", PackagePlus],
  ["/admin/payments", "Payments", WalletCards],
  ["/admin/reports", "Reports", BarChart3],
  ["/admin/audit", "Audit Log", ReceiptText],
  ["/admin/ssh-setup", "SSH / PuTTY", Terminal],
  ["/admin/settings", "Settings", Settings],
] as const;

function ErrorNotice({ message }: { message: string }) {
  return (
    <div className="admin-error" role="alert">
      <LockKeyhole />
      <div>
        <strong>Unable to load this secure area</strong>
        <span>{message}</span>
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="admin-loading">
      <span />
      <span />
      <span />
    </div>
  );
}

function Status({ value }: { value: unknown }) {
  const label = String(value || "draft").toLowerCase();
  const status = label.replace(/[\s_]+/g, "-");
  return (
    <span className={`admin-status status-${status}`}>{titleCase(label)}</span>
  );
}

function Dialog({
  title,
  eyebrow,
  onClose,
  children,
  className = "",
}: {
  title: string;
  eyebrow: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);
  return (
    <div
      className="admin-dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={`admin-dialog ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header>
          <div>
            <span>{eyebrow}</span>
            <h2>{title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function CustomerDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial?: RecordRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const defaults = useApi<{ settings: RecordRow }>(
    initial ? "" : "invoice-settings",
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [taxExempt, setTaxExempt] = useState(Number(initial?.tax_exempt) === 1);
  const [taxRate, setTaxRate] = useState(
    Number(initial?.sales_tax_rate_bps ?? 600) / 100,
  );
  const editing = Boolean(initial);
  useEffect(() => {
    if (!initial && defaults.data?.settings)
      setTaxRate(
        Number(defaults.data.settings.default_sales_tax_rate_bps ?? 600) / 100,
      );
  }, [defaults.data, initial]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api(editing ? `customers/${String(initial?.id)}` : "customers", {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify({
          companyName: form.get("companyName"),
          contactName: form.get("contactName"),
          contactTitle: form.get("contactTitle"),
          billingEmail: form.get("billingEmail"),
          phone: form.get("phone"),
          billingAddress: form.get("billingAddress"),
          serviceAddress: form.get("serviceAddress"),
          paymentTermsDays: Number(form.get("paymentTermsDays")),
          taxExempt,
          salesTaxRateBps: Math.round(taxRate * 100),
          status: form.get("status"),
          internalNotes: form.get("internalNotes"),
        }),
      });
      onSaved();
      onClose();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to save customer.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <Dialog
      title={editing ? "Edit customer" : "Add customer"}
      eyebrow="Customer database"
      onClose={onClose}
    >
      {defaults.loading ? (
        <Loading />
      ) : defaults.error ? (
        <ErrorNotice message={defaults.error} />
      ) : (
        <form className="admin-form" onSubmit={submit}>
          <div className="admin-field-grid">
            {editing && (
              <label>
                Customer number
                <input value={String(initial?.customer_number || "")} readOnly />
                <small>Permanent identifier used by central CSV imports.</small>
              </label>
            )}
            <label>
              Company name *
              <input
                name="companyName"
                required
                maxLength={160}
                autoFocus
                defaultValue={String(initial?.company_name || "")}
              />
            </label>
            <label>
              Contact name *
              <input
                name="contactName"
                required
                maxLength={160}
                defaultValue={String(initial?.contact_name || "")}
              />
            </label>
            <label>
              Contact title
              <input
                name="contactTitle"
                maxLength={120}
                defaultValue={String(initial?.contact_title || "")}
              />
            </label>
            <label>
              Billing email *
              <input
                name="billingEmail"
                type="email"
                required
                maxLength={200}
                defaultValue={String(initial?.billing_email || "")}
              />
            </label>
            <label>
              Phone
              <input
                name="phone"
                type="tel"
                maxLength={50}
                defaultValue={String(initial?.phone || "")}
              />
            </label>
            <label>
              Payment terms
              <select
                name="paymentTermsDays"
                defaultValue={String(initial?.payment_terms_days ?? 30)}
              >
                <option value="0">Due on receipt</option>
                <option value="15">Net 15</option>
                <option value="30">Net 30</option>
                <option value="45">Net 45</option>
                <option value="60">Net 60</option>
              </select>
            </label>
            <label>
              Michigan sales tax (%)
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={taxRate}
                onChange={(event) => setTaxRate(Number(event.target.value))}
                disabled={taxExempt}
              />
              <small>
                {taxExempt
                  ? "Excluded because this organization is tax-exempt."
                  : "Defaults to Michigan's configured sales-tax percentage."}
              </small>
            </label>
            <label>
              Account status
              <select
                name="status"
                defaultValue={String(initial?.status || "active")}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
          </div>
          <div className="admin-field-grid">
            <label>
              Billing address
              <textarea
                name="billingAddress"
                rows={4}
                maxLength={1000}
                placeholder="Street&#10;City, State ZIP"
                defaultValue={String(initial?.billing_address || "")}
              />
            </label>
            <label>
              Service address
              <textarea
                name="serviceAddress"
                rows={4}
                maxLength={1000}
                placeholder="Leave blank when the same as billing"
                defaultValue={String(initial?.service_address || "")}
              />
            </label>
          </div>
          <label>
            Internal notes
            <textarea
              name="internalNotes"
              rows={4}
              maxLength={3000}
              defaultValue={String(initial?.internal_notes || "")}
            />
          </label>
          <label className="admin-checkbox">
            <input
              name="taxExempt"
              type="checkbox"
              value="yes"
              checked={taxExempt}
              onChange={(event) => setTaxExempt(event.target.checked)}
            />
            <span>
              Tax-exempt organization — exclude sales tax from invoices
            </span>
          </label>
          {error && <p className="admin-form-error">{error}</p>}
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-button secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button className="admin-button primary" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save customer" : "Add customer"}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

function SkuDialog({
  initial,
  cloning = false,
  onClose,
  onSaved,
}: {
  initial?: RecordRow;
  cloning?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const categories = useApi<{ categories: RecordRow[] }>("categories");
  const [error, setError] = useState("");
  const [savingMode, setSavingMode] = useState<"" | "current" | "new">("");
  const editing = Boolean(initial) && !cloning;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submitter = (event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
    const saveMode = submitter?.value === "new" ? "new" : "current";
    setSavingMode(saveMode);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const payload: RecordRow = {
          sku: form.get("sku"),
          name: form.get("name"),
          description: form.get("description"),
          categoryId: Number(form.get("categoryId")),
          serviceScope: form.get("serviceScope"),
          unitPriceCents: Math.round(Number(form.get("unitPrice")) * 100),
          billingPeriod: form.get("billingPeriod"),
          unitLabel: form.get("unitLabel"),
          minimumQuantity: Number(form.get("minimumQuantity")),
          maximumQuantity: Number(form.get("maximumQuantity")),
          quantityStep: Number(form.get("quantityStep")),
          sortOrder: Number(form.get("sortOrder")),
          active: form.get("active") === "yes",
          visibleManagedConfigurator: form.get("visible") === "yes",
          changeSummary: form.get("changeSummary"),
          expectedRevisionKey: initial?.current_revision_key,
      };
      if (!editing) {
        await api("skus", { method: "POST", body: JSON.stringify(payload) });
      } else {
        await api(`skus/${String(initial?.id)}`, {
          method: "PUT",
          body: JSON.stringify({
            ...payload,
            saveMode,
            confirmed: saveMode === "current",
          }),
        });
      }
      onSaved();
      onClose();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to save SKU.",
      );
    } finally {
      setSavingMode("");
    }
  }
  return (
    <Dialog
      title={editing ? "Edit current SKU revision" : cloning ? "Clone SKU" : "Add SKU"}
      eyebrow="Service catalog"
      onClose={onClose}
    >
      {categories.loading ? (
        <Loading />
      ) : categories.error ? (
        <ErrorNotice message={categories.error} />
      ) : (
        <form className="admin-form" onSubmit={submit}>
          <div className="admin-field-grid">
            <label>
              SKU code *
              <input
                name="sku"
                required
                maxLength={50}
                placeholder="MFW-001"
                autoFocus
                defaultValue={cloning ? "" : String(initial?.sku || "")}
                readOnly={editing}
              />
              <small>
                {editing
                  ? "Stable identity—create a different SKU if this code must change."
                  : cloning
                    ? `Enter the unique code for this copy of ${String(initial?.sku || "the source SKU")}.`
                  : "This becomes the stable customer-facing identity."}
              </small>
            </label>
            <label>
              Name *
              <input
                name="name"
                required
                maxLength={160}
                defaultValue={String(initial?.name || "")}
              />
            </label>
            <label>
              Category *
              <select
                name="categoryId"
                required
                defaultValue={String(initial?.category_id || "")}
              >
                <option value="" disabled>
                  Select category
                </option>
                {categories.data?.categories.map((row) => (
                  <option key={String(row.id)} value={String(row.id)}>
                    {String(row.name)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Scope
              <select
                name="serviceScope"
                defaultValue={String(initial?.service_scope || "managed")}
              >
                <option value="managed">Managed service</option>
                <option value="project">Project</option>
                <option value="hourly">Hourly</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label>
              Unit price *
              <input
                name="unitPrice"
                type="number"
                min="0"
                step="0.01"
                required
                defaultValue={
                  initial ? Number(initial.unit_price_cents) / 100 : ""
                }
              />
            </label>
            <label>
              Billing period
              <select
                name="billingPeriod"
                defaultValue={String(initial?.billing_period || "month")}
              >
                <option value="month">Monthly</option>
                <option value="year">Annual</option>
                <option value="one_time">One-time</option>
              </select>
            </label>
            <label>
              Unit label
              <input
                name="unitLabel"
                placeholder="/device/month"
                defaultValue={String(initial?.unit_label || "")}
              />
            </label>
            <label>
              Sort order
              <input
                name="sortOrder"
                type="number"
                min="0"
                step="1"
                defaultValue={Number(initial?.sort_order || 0)}
              />
            </label>
            <label>
              Minimum quantity
              <input
                name="minimumQuantity"
                type="number"
                min="1"
                step="1"
                defaultValue={Number(initial?.minimum_quantity || 1)}
              />
            </label>
            <label>
              Maximum quantity
              <input
                name="maximumQuantity"
                type="number"
                min="1"
                step="1"
                defaultValue={Number(initial?.maximum_quantity || 1000)}
              />
            </label>
            <label>
              Quantity step
              <input
                name="quantityStep"
                type="number"
                min="1"
                step="1"
                defaultValue={Number(initial?.quantity_step || 1)}
              />
            </label>
          </div>
          {cloning && (
            <p className="admin-form-note sku-clone-note">
              <Copy /> Copied from <strong>{String(initial?.sku)}</strong>. Review the fields below, then save to create a separate SKU with its own revision history.
            </p>
          )}
          <label>
            Description *
            <textarea
              name="description"
              rows={4}
              required
              maxLength={1000}
              defaultValue={String(initial?.description || "")}
            />
          </label>
          {editing && (
            <label>
              Revision summary
              <input
                name="changeSummary"
                maxLength={500}
                placeholder="Price update, wording change, scope adjustment…"
              />
            </label>
          )}
          <div className="admin-field-grid">
            <label className="admin-checkbox">
              <input
                name="active"
                type="checkbox"
                value="yes"
                defaultChecked={!initial || Number(initial.active) === 1}
              />
              <span>SKU is active</span>
            </label>
            <label className="admin-checkbox">
              <input
                name="visible"
                type="checkbox"
                value="yes"
                defaultChecked={
                  Number(initial?.visible_managed_configurator) === 1
                }
              />
              <span>Visible in managed-services configurator</span>
            </label>
          </div>
          {error && <p className="admin-form-error">{error}</p>}
          <div className="admin-form-actions">
            <button className="admin-button primary" name="saveMode" value="current" disabled={Boolean(savingMode)}>
              {savingMode === "current" ? "Saving…" : editing ? "Save" : cloning ? "Create cloned SKU" : "Add SKU"}
            </button>
            {editing && (
              <button className="admin-button secondary" name="saveMode" value="new" disabled={Boolean(savingMode)}>
                {savingMode === "new" ? "Saving…" : "Save to New Revision"}
              </button>
            )}
            <button
              type="button"
              className="admin-button secondary"
              disabled={Boolean(savingMode)}
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

type InvoiceLineState = {
  itemId?: string;
  skuId: string;
  skuRevisionKey?: string;
  pricingSource?: string;
  quantity: number;
  description?: string;
  unitPriceCents?: number;
};

function InvoiceDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial?: RecordRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const customers = useApi<{ customers: RecordRow[] }>("customers");
  const skus = useApi<{ skus: RecordRow[] }>("skus");
  const [detailRefresh, setDetailRefresh] = useState(0);
  const detail = useApi<{
    invoice: RecordRow;
    items: RecordRow[];
    notifications: RecordRow[];
    editable: boolean;
  }>(initial ? `invoices/${String(initial.id)}` : "", detailRefresh);
  const [lines, setLines] = useState<InvoiceLineState[]>([
    { skuId: "", quantity: 1 },
  ]);
  const [customerId, setCustomerId] = useState("");
  const [discount, setDiscount] = useState(0);
  const [showQuickPayment, setShowQuickPayment] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const editing = Boolean(initial);
  const invoice = detail.data?.invoice;
  useEffect(() => {
    if (!detail.data) return;
    setCustomerId(String(detail.data.invoice.customer_id));
    setDiscount(Number(detail.data.invoice.discount_cents || 0) / 100);
    setLines(
      detail.data.items.map((item) => ({
        itemId: String(item.id),
        skuId: item.sku_id ? String(item.sku_id) : "",
        skuRevisionKey: String(item.sku_revision_key || ""),
        pricingSource: String(item.pricing_source || ""),
        quantity: Number(item.quantity),
        description: String(item.description),
        unitPriceCents: Number(item.unit_price_cents),
      })),
    );
  }, [detail.data]);
  const dueDefault = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().slice(0, 10);
  }, []);
  const customer = customers.data?.customers.find(
    (row) => String(row.id) === customerId,
  );
  const subtotalCents = lines.reduce((sum, line) => {
    const sku = skus.data?.skus.find((row) => String(row.id) === line.skuId);
    return (
      sum +
      Math.round(
        line.quantity *
          Number(line.unitPriceCents ?? sku?.unit_price_cents ?? 0),
      )
    );
  }, 0);
  const taxRateBps =
    Number(customer?.tax_exempt) === 1
      ? 0
      : Number(customer?.sales_tax_rate_bps || 0);
  const taxCents = Math.round(
    (Math.max(0, subtotalCents - Math.round(discount * 100)) * taxRateBps) /
      10_000,
  );
  const invoiceLinePayload = lines.map((line) => {
    const selected = skus.data?.skus.find(
      (row) => String(row.id) === line.skuId,
    );
    const historicalRevisionStillMatches = Boolean(
      line.skuRevisionKey &&
        selected &&
        line.skuRevisionKey.startsWith(`${String(selected.sku)}@`),
    );
    return {
      itemId: line.itemId,
      skuId: line.skuId ? Number(line.skuId) : undefined,
      skuRevisionKey: historicalRevisionStillMatches
        ? line.skuRevisionKey
        : undefined,
      description: line.description,
      unitPriceCents: line.unitPriceCents,
      pricingSource: line.pricingSource,
      quantity: line.quantity,
    };
  });
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api(editing ? `invoices/${String(initial?.id)}` : "invoices", {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify({
          customerId: form.get("customerId"),
          issueDate: form.get("issueDate"),
          dueDate: form.get("dueDate"),
          purchaseOrder: form.get("purchaseOrder"),
          discountCents: Math.round(Number(form.get("discount")) * 100),
          publicNotes: form.get("publicNotes"),
          internalNotes: form.get("internalNotes"),
          items: invoiceLinePayload,
        }),
      });
      onSaved();
      onClose();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to save invoice.",
      );
    } finally {
      setSaving(false);
    }
  }
  async function action(name: "approve" | "email" | "postal" | "cancel") {
    if (
      name === "cancel" &&
      !window.confirm(
        `Cancel ${String(invoice?.invoice_number)}? Its history will be retained.`,
      )
    )
      return;
    setBusy(name);
    setError("");
    setNotice("");
    try {
      const result = await api<{ message: string }>(
        `invoices/${String(initial?.id)}/${name}`,
        { method: "POST", body: "{}" },
      );
      setNotice(result.message);
      setDetailRefresh((value) => value + 1);
      onSaved();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to update invoice.",
      );
    } finally {
      setBusy("");
    }
  }
  async function duplicateAsDraft() {
    setBusy("duplicate");
    setError("");
    setNotice("");
    try {
      const result = await api<{ message: string }>(
        `invoices/${String(initial?.id)}/duplicate`,
        { method: "POST", body: "{}" },
      );
      setNotice(result.message);
      onSaved();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Unable to duplicate invoice.",
      );
    } finally {
      setBusy("");
    }
  }
  async function recordFullPayment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      !window.confirm(
        `Record the full remaining balance of ${currency(outstandingCents)} for ${String(invoice?.invoice_number)}?`,
      )
    )
      return;
    setBusy("payment");
    setError("");
    setNotice("");
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<{ message: string }>(
        `invoices/${String(initial?.id)}/payment`,
        {
          method: "POST",
          body: JSON.stringify({
            receivedDate: form.get("receivedDate"),
            method: form.get("method"),
            reference: form.get("reference"),
            notes: "Full payment recorded from invoice detail.",
          }),
        },
      );
      setNotice(result.message);
      setShowQuickPayment(false);
      setDetailRefresh((value) => value + 1);
      onSaved();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to record payment.",
      );
    } finally {
      setBusy("");
    }
  }
  const loading =
    customers.loading || skus.loading || (editing && detail.loading);
  const loadError = customers.error || skus.error || detail.error;
  const canNotify =
    invoice?.approval_status === "approved" &&
    invoice?.status !== "void" &&
    (Number(invoice?.correction_pending_resend) === 1 ||
      (!["paid", "partial"].includes(String(invoice?.status)) && Number(invoice?.paid_cents) === 0));
  const outstandingCents = Math.max(
    0,
    Number(invoice?.total_cents || 0) - Number(invoice?.paid_cents || 0),
  );
  const canRecordPayment =
    editing &&
    !["void", "paid"].includes(String(invoice?.status)) &&
    outstandingCents > 0;
  return (
    <Dialog
      title={
        editing ? String(invoice?.invoice_number || "Invoice") : "New invoice"
      }
      eyebrow={editing ? "Invoice detail" : "Billing"}
      onClose={onClose}
    >
      {loading ? (
        <Loading />
      ) : loadError ? (
        <ErrorNotice message={loadError} />
      ) : (
        <>
          {editing && (
            <div className="invoice-action-bar">
              <a
                className="admin-button secondary"
                href={invoicePdfUrl(initial?.id)}
              >
                <Download /> Download PDF
              </a>
              <button
                className="admin-button secondary"
                type="button"
                disabled={Boolean(busy)}
                onClick={duplicateAsDraft}
              >
                <Copy />{" "}
                {busy === "duplicate" ? "Duplicating…" : "Duplicate as draft"}
              </button>
              {invoice?.approval_status !== "approved" &&
                detail.data?.editable && (
                  <button
                    className="admin-button secondary"
                    type="button"
                    disabled={Boolean(busy)}
                    onClick={() => action("approve")}
                  >
                    <CheckCircle2 />{" "}
                    {busy === "approve" ? "Approving…" : "Approve"}
                  </button>
                )}
              <button
                className="admin-button secondary"
                type="button"
                disabled={!canNotify || Boolean(busy)}
                onClick={() => action("email")}
              >
                <Mail /> {busy === "email" ? "Sending…" : "Email PDF"}
              </button>
              <button
                className="admin-button secondary"
                type="button"
                disabled={!canNotify || Boolean(busy)}
                onClick={() => action("postal")}
              >
                <Stamp /> {busy === "postal" ? "Recording…" : "Postal mailed"}
              </button>
              {canRecordPayment && (
                <button
                  className="admin-button payment-button"
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => setShowQuickPayment((shown) => !shown)}
                >
                  <CircleDollarSign />{" "}
                  {showQuickPayment ? "Close payment" : "Mark paid"}
                </button>
              )}
              {invoice?.status !== "void" && detail.data?.editable && (
                <button
                  className="admin-button danger-button"
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => action("cancel")}
                >
                  <Ban /> Cancel invoice
                </button>
              )}
            </div>
          )}
          {editing && Number(invoice?.correction_pending_resend) === 1 && (
            <p className="invoice-correction-warning">
              This invoice changed after customer delivery because its SKU revision was corrected.
              Review the PDF and resend or record postal delivery. The previous delivery timestamp remains in the audit history.
            </p>
          )}
          {showQuickPayment && canRecordPayment && (
            <form
              className="invoice-quick-payment"
              onSubmit={recordFullPayment}
            >
              <div>
                <strong>Record full payment</strong>
                <span>
                  This creates a {currency(outstandingCents)} payment and marks
                  the invoice paid.
                </span>
              </div>
              <label>
                Received date
                <input
                  name="receivedDate"
                  type="date"
                  defaultValue={new Date().toISOString().slice(0, 10)}
                  required
                />
              </label>
              <label>
                Method
                <select name="method" defaultValue="check">
                  <option value="check">Check</option>
                  <option value="ach">ACH</option>
                  <option value="cash">Cash</option>
                  <option value="card_provider">Card provider</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>
                Reference
                <input
                  name="reference"
                  maxLength={120}
                  placeholder="Check or transaction number"
                />
              </label>
              <button className="admin-button primary" disabled={Boolean(busy)}>
                <CircleDollarSign />{" "}
                {busy === "payment"
                  ? "Recording…"
                  : `Record ${currency(outstandingCents)}`}
              </button>
            </form>
          )}
          {notice && (
            <p className="admin-form-success dialog-message">{notice}</p>
          )}
          <form className="admin-form" onSubmit={submit}>
            <div className="admin-field-grid">
              <label>
                Customer *
                <select
                  name="customerId"
                  required
                  value={customerId}
                  onChange={(event) => setCustomerId(event.target.value)}
                  disabled={editing && !detail.data?.editable}
                >
                  <option value="" disabled>
                    Select customer
                  </option>
                  {customers.data?.customers
                    .filter(
                      (row) =>
                        row.status === "active" ||
                        String(row.id) === customerId,
                    )
                    .map((row) => (
                      <option key={String(row.id)} value={String(row.id)}>
                        {String(row.company_name)}
                        {Number(row.tax_exempt) === 1
                          ? " · Tax exempt"
                          : ` · ${Number(row.sales_tax_rate_bps || 0) / 100}% tax`}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Issue date
                <input
                  name="issueDate"
                  type="date"
                  defaultValue={String(
                    invoice?.issue_date ||
                      new Date().toISOString().slice(0, 10),
                  )}
                  required
                  disabled={editing && !detail.data?.editable}
                />
              </label>
              <label>
                Due date
                <input
                  name="dueDate"
                  type="date"
                  defaultValue={String(invoice?.due_date || dueDefault)}
                  required
                  disabled={editing && !detail.data?.editable}
                />
              </label>
              <label>
                Purchase order
                <input
                  name="purchaseOrder"
                  maxLength={120}
                  defaultValue={String(invoice?.purchase_order || "")}
                  disabled={editing && !detail.data?.editable}
                />
              </label>
              <label>
                Discount
                <input
                  name="discount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={discount}
                  onChange={(event) => setDiscount(Number(event.target.value))}
                  disabled={editing && !detail.data?.editable}
                />
              </label>
            </div>
            <div className="invoice-lines">
              <div className="invoice-lines-heading">
                <strong>Line items</strong>
                {(!editing || detail.data?.editable) && (
                  <button
                    type="button"
                    onClick={() =>
                      setLines([...lines, { skuId: "", quantity: 1 }])
                    }
                  >
                    <Plus /> Add line
                  </button>
                )}
              </div>
              {lines.map((line, index) => (
                <div className="invoice-line" key={index}>
                  <select
                    aria-label={`SKU for line ${index + 1}`}
                    value={line.skuId}
                    onChange={(event) => {
                      const sku = skus.data?.skus.find(
                        (row) => String(row.id) === event.target.value,
                      );
                      setLines(
                        lines.map((current, lineIndex) =>
                          lineIndex === index
                            ? {
                                ...current,
                                skuId: event.target.value,
                                skuRevisionKey: "",
                                pricingSource: "billing",
                                description: String(sku?.name || ""),
                                unitPriceCents: Number(
                                  sku?.unit_price_cents || 0,
                                ),
                              }
                            : current,
                        ),
                      );
                    }}
                    required={!line.description}
                    disabled={editing && !detail.data?.editable}
                  >
                    {!line.skuId && (
                      <option value="">
                        {line.description
                          ? `${line.description} · catalog item unavailable`
                          : "Select a SKU"}
                      </option>
                    )}
                    {skus.data?.skus.map((row) => (
                      <option key={String(row.id)} value={String(row.id)}>
                        {String(row.sku)} · {String(row.name)} ·{" "}
                        {currency(
                          String(row.id) === line.skuId &&
                            line.pricingSource === "informational"
                            ? 0
                            : row.unit_price_cents,
                        )}
                        {String(row.id) === line.skuId &&
                        line.pricingSource === "informational"
                          ? " · informational"
                          : ""}
                        {Number(row.active) ? "" : " · inactive"}
                      </option>
                    ))}
                  </select>
                  <input
                    aria-label={`Quantity for line ${index + 1}`}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={line.quantity}
                    onChange={(event) =>
                      setLines(
                        lines.map((current, lineIndex) =>
                          lineIndex === index
                            ? {
                                ...current,
                                quantity: Number(event.target.value),
                              }
                            : current,
                        ),
                      )
                    }
                    required
                    disabled={editing && !detail.data?.editable}
                  />
                  {lines.length > 1 && (!editing || detail.data?.editable) && (
                    <button
                      type="button"
                      aria-label={`Remove line ${index + 1}`}
                      onClick={() =>
                        setLines(
                          lines.filter((_, lineIndex) => lineIndex !== index),
                        )
                      }
                    >
                      <X />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="invoice-calculation">
              <span>
                Subtotal <strong>{currency(subtotalCents)}</strong>
              </span>
              <span>
                {taxRateBps
                  ? `Sales tax (${(taxRateBps / 100).toFixed(2)}%)`
                  : "Sales tax (excluded)"}{" "}
                <strong>{currency(taxCents)}</strong>
              </span>
              <span>
                Total{" "}
                <strong>
                  {currency(
                    subtotalCents - Math.round(discount * 100) + taxCents,
                  )}
                </strong>
              </span>
            </div>
            <label>
              Customer-facing notes
              <textarea
                name="publicNotes"
                rows={3}
                maxLength={3000}
                defaultValue={String(invoice?.public_notes || "")}
                disabled={editing && !detail.data?.editable}
              />
            </label>
            <label>
              Internal notes
              <textarea
                name="internalNotes"
                rows={3}
                maxLength={3000}
                defaultValue={String(invoice?.internal_notes || "")}
                disabled={editing && !detail.data?.editable}
              />
            </label>
            {editing && !detail.data?.editable && (
              <p className="admin-form-note">
                This invoice was delivered, has a payment, or was canceled, so
                its financial details are locked. Cancel and replace a delivered
                invoice when a correction is required.
              </p>
            )}
            {error && <p className="admin-form-error">{error}</p>}
            <div className="admin-form-actions">
              <button
                type="button"
                className="admin-button secondary"
                onClick={onClose}
              >
                Close
              </button>
              {(!editing || detail.data?.editable) && (
                <button className="admin-button primary" disabled={saving}>
                  {saving
                    ? "Saving…"
                    : editing
                      ? "Save invoice"
                      : "Create draft invoice"}
                </button>
              )}
            </div>
          </form>
          {editing && Boolean(detail.data?.notifications.length) && (
            <section className="notification-history">
              <h3>Customer notification history</h3>
              {detail.data?.notifications.map((row) => (
                <div key={String(row.id)}>
                  <span>
                    <strong>
                      {row.channel === "email" ? "Email" : "Postal mail"}
                    </strong>{" "}
                    · {titleCase(row.delivery_status)}
                    {row.recipient ? ` · ${String(row.recipient)}` : ""}
                  </span>
                  <time>{String(row.created_at)}</time>
                </div>
              ))}
            </section>
          )}
        </>
      )}
    </Dialog>
  );
}

function PackageDialog({
  initial,
  cloning = false,
  onClose,
  onSaved,
}: {
  initial?: RecordRow;
  cloning?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const skus = useApi<{ skus: RecordRow[] }>("skus");
  const initialItems = Array.isArray(initial?.items)
    ? (initial.items as RecordRow[])
    : [];
  const [lines, setLines] = useState(
    initialItems.length
      ? initialItems.map((item) => ({
          skuId: String(item.sku_id),
          quantity: Number(item.quantity),
        }))
      : [{ skuId: "", quantity: 1 }],
  );
  const initialFeatures = Array.isArray(initial?.features)
    ? (initial.features as RecordRow[])
    : [];
  const [features, setFeatures] = useState(
    initialFeatures.map((feature, index) => ({
      id: String(feature.id || `existing-${index}`),
      text: String(feature.feature_text),
    })),
  );
  const [draggedFeatureIndex, setDraggedFeatureIndex] = useState<number | null>(null);
  const [featureDropIndex, setFeatureDropIndex] = useState<number | null>(null);
  const [pricingMode, setPricingMode] = useState(
    String(initial?.pricing_mode || "fixed"),
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initial) && !cloning;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api(editing ? `bundles/${String(initial?.id)}` : "bundles", {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify({
          code: form.get("code"),
          name: form.get("name"),
          eyebrow: form.get("eyebrow"),
          description: form.get("description"),
          pricingPrefix: form.get("pricingPrefix"),
          pricingMode,
          priceCents: pricingMode === "fixed"
            ? Math.round(Number(form.get("price")) * 100)
            : null,
          billingPeriod: form.get("billingPeriod"),
          billingSuffix: form.get("billingSuffix"),
          featured: form.get("featured") === "yes",
          featuredLabel: form.get("featuredLabel"),
          visibleManagedPricing: form.get("visible") === "yes",
          active: form.get("active") === "yes",
          sortOrder: Number(form.get("sortOrder")),
          ctaLabel: form.get("ctaLabel"),
          ctaPath: form.get("ctaPath"),
          items: lines.map((line) => ({
            skuId: Number(line.skuId),
            quantity: line.quantity,
          })),
          features: features.map((feature) => ({ featureText: feature.text })),
        }),
      });
      onSaved();
      onClose();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to save package.",
      );
    } finally {
      setSaving(false);
    }
  }
  const selectedIds = new Set(lines.map((line) => line.skuId));
  const calculatedPriceCents = lines.reduce((total, line) => {
    const sku = skus.data?.skus.find((row) => String(row.id) === line.skuId);
    return total + Number(sku?.unit_price_cents || 0) * Number(line.quantity || 0);
  }, 0);
  function moveFeature(fromIndex: number, toIndex: number) {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 ||
      fromIndex >= features.length || toIndex >= features.length) return;
    setFeatures((current) => {
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }
  function dropFeature(toIndex: number) {
    if (draggedFeatureIndex !== null) moveFeature(draggedFeatureIndex, toIndex);
    setDraggedFeatureIndex(null);
    setFeatureDropIndex(null);
  }
  return (
    <Dialog
      title={editing ? "Edit package" : cloning ? "Clone package" : "Build package"}
      eyebrow="Managed services"
      onClose={onClose}
    >
      {skus.loading ? (
        <Loading />
      ) : skus.error ? (
        <ErrorNotice message={skus.error} />
      ) : (
        <form className="admin-form" onSubmit={submit}>
          <div className="admin-field-grid">
            <label>
              Package code *
              <input
                name="code"
                required
                maxLength={50}
                placeholder="MS-PRO"
                autoFocus
                defaultValue={cloning ? "" : String(initial?.code || "")}
              />
            </label>
            <label>
              Package name *
              <input
                name="name"
                required
                maxLength={160}
                defaultValue={String(initial?.name || "")}
              />
            </label>
            <label>
              Eyebrow text
              <input
                name="eyebrow"
                maxLength={120}
                placeholder="Managed business"
                defaultValue={String(initial?.eyebrow || "")}
              />
            </label>
            <label>
              Pricing mode
              <select
                name="pricingMode"
                value={pricingMode}
                onChange={(event) => setPricingMode(event.target.value)}
              >
                <option value="fixed">Fixed price</option>
                <option value="sum_components">Calculated from SKUs</option>
              </select>
            </label>
            <label>
              Package price
              {pricingMode === "sum_components" ? (
                <input name="calculatedPrice" type="number" readOnly value={calculatedPriceCents / 100} />
              ) : (
                <input name="price" type="number" min="0" step="0.01" required
                  defaultValue={initial ? Number(initial.price_cents || 0) / 100 : ""} />
              )}
              {pricingMode === "sum_components" && <small>Automatically calculated from the included SKU quantities.</small>}
            </label>
            <label>
              Billing period
              <select
                name="billingPeriod"
                defaultValue={String(initial?.billing_period || "month")}
              >
                <option value="month">Monthly</option>
                <option value="year">Annual</option>
                <option value="one_time">One-time</option>
              </select>
            </label>
            <label>
              Pricing prefix
              <input
                name="pricingPrefix"
                maxLength={80}
                defaultValue={String(initial?.pricing_prefix || "Starting at")}
              />
            </label>
            <label>
              Billing suffix
              <input
                name="billingSuffix"
                maxLength={80}
                defaultValue={String(initial?.billing_suffix || "/month")}
              />
            </label>
            <label>
              Sort order
              <input
                name="sortOrder"
                type="number"
                min="0"
                step="1"
                defaultValue={Number(initial?.sort_order || 0)}
              />
            </label>
          </div>
          <label>
            Description *
            <textarea
              name="description"
              rows={3}
              required
              maxLength={1200}
              defaultValue={String(initial?.description || "")}
            />
          </label>
          <div className="invoice-lines">
            <div className="invoice-lines-heading">
              <strong>Included SKUs</strong>
              <button
                type="button"
                onClick={() =>
                  setLines([
                    ...lines,
                    { skuId: "", quantity: 1 },
                  ])
                }
              >
                <Plus /> Add SKU
              </button>
            </div>
            {lines.map((line, index) => (
              <div className="package-line" key={index}>
                <select
                  aria-label={`Package SKU ${index + 1}`}
                  value={line.skuId}
                  onChange={(event) =>
                    setLines(
                      lines.map((current, lineIndex) =>
                        lineIndex === index
                          ? { ...current, skuId: event.target.value }
                          : current,
                      ),
                    )
                  }
                  required
                >
                  <option value="" disabled>
                    Select SKU
                  </option>
                  {skus.data?.skus
                    .filter(
                      (row) =>
                        Number(row.active) === 1 ||
                        selectedIds.has(String(row.id)),
                    )
                    .map((row) => (
                      <option key={String(row.id)} value={String(row.id)}>
                        {String(row.sku)} · {String(row.name)}
                        {Number(row.active) ? "" : " (inactive)"}
                      </option>
                    ))}
                </select>
                <input
                  type="number"
                  aria-label={`Package quantity ${index + 1}`}
                  min="1"
                  step="1"
                  value={line.quantity}
                  onChange={(event) =>
                    setLines(
                      lines.map((current, lineIndex) =>
                        lineIndex === index
                          ? { ...current, quantity: Number(event.target.value) }
                          : current,
                      ),
                    )
                  }
                  required
                />
                {lines.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Remove package line ${index + 1}`}
                    onClick={() =>
                      setLines(
                        lines.filter((_, lineIndex) => lineIndex !== index),
                      )
                    }
                  >
                    <X />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="invoice-lines">
            <div className="invoice-lines-heading">
              <div><strong>Website feature list</strong><small>These items populate the public package card and are independent of billing SKUs.</small></div>
              <button type="button" onClick={() => setFeatures([...features, { id: crypto.randomUUID(), text: "" }])}>
                <Plus /> Add feature
              </button>
            </div>
            {features.map((feature, index) => (
              <div
                className={`package-feature-line${draggedFeatureIndex === index ? " dragging" : ""}${featureDropIndex === index ? " drop-target" : ""}`}
                key={feature.id}
                onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "move"; setFeatureDropIndex(index); }}
                onDragLeave={() => setFeatureDropIndex((current) => current === index ? null : current)}
                onDrop={(event) => { event.preventDefault(); dropFeature(index); }}
              >
                <span
                  className="package-feature-drag"
                  role="button"
                  tabIndex={0}
                  draggable
                  aria-label={`Reorder website feature ${index + 1}. Use drag and drop or the up and down arrow keys.`}
                  title="Drag to reorder"
                  onDragStart={(event) => {
                    setDraggedFeatureIndex(index);
                    setFeatureDropIndex(index);
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", feature.id);
                  }}
                  onDragEnd={() => { setDraggedFeatureIndex(null); setFeatureDropIndex(null); }}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowUp" && index > 0) { event.preventDefault(); moveFeature(index, index - 1); }
                    if (event.key === "ArrowDown" && index < features.length - 1) { event.preventDefault(); moveFeature(index, index + 1); }
                  }}
                >
                  <GripVertical />
                </span>
                <input
                  aria-label={`Website feature ${index + 1}`}
                  placeholder="Website feature text"
                  maxLength={300}
                  value={feature.text}
                  onChange={(event) => setFeatures(features.map((current, featureIndex) =>
                    featureIndex === index ? { ...current, text: event.target.value } : current))}
                  required
                />
                <button type="button" aria-label={`Remove website feature ${index + 1}`}
                  onClick={() => setFeatures(features.filter((_, featureIndex) => featureIndex !== index))}>
                  <X />
                </button>
              </div>
            ))}
          </div>
          <div className="admin-field-grid">
            <label className="admin-checkbox">
              <input
                name="active"
                type="checkbox"
                value="yes"
                defaultChecked={!initial || Number(initial.active) === 1}
              />
              <span>Package is active</span>
            </label>
            <label className="admin-checkbox">
              <input
                name="visible"
                type="checkbox"
                value="yes"
                defaultChecked={
                  !initial || Number(initial.visible_managed_pricing) === 1
                }
              />
              <span>Visible on managed-services pricing</span>
            </label>
            <label className="admin-checkbox">
              <input
                name="featured"
                type="checkbox"
                value="yes"
                defaultChecked={Number(initial?.featured) === 1}
              />
              <span>Feature this package</span>
            </label>
            <label>
              Featured label
              <input
                name="featuredLabel"
                maxLength={120}
                defaultValue={String(initial?.featured_label || "Most popular")}
              />
            </label>
            <label>
              Button label
              <input
                name="ctaLabel"
                maxLength={120}
                defaultValue={String(initial?.cta_label || "Discuss this plan")}
              />
            </label>
            <label>
              Button path
              <input
                name="ctaPath"
                maxLength={240}
                defaultValue={String(initial?.cta_path || "/contact")}
              />
            </label>
          </div>
          {error && <p className="admin-form-error">{error}</p>}
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-button secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button className="admin-button primary" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save package" : cloning ? "Create package copy" : "Create package"}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

function CategoryManagerDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [refresh, setRefresh] = useState(0);
  const {
    data,
    error: loadError,
    loading,
  } = useApi<{ categories: RecordRow[] }>("categories", refresh);
  const [editing, setEditing] = useState<RecordRow | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      await api(editing ? `categories/${String(editing.id)}` : "categories", {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify({
          name: form.get("name"),
          description: form.get("description"),
          sortOrder: Number(form.get("sortOrder")),
        }),
      });
      setEditing(null);
      setRefresh((value) => value + 1);
      onSaved();
      formElement.reset();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to save category.",
      );
    } finally {
      setSaving(false);
    }
  }
  async function remove(row: RecordRow) {
    if (!window.confirm(`Delete the unused category “${String(row.name)}”?`))
      return;
    setError("");
    try {
      await api(`categories/${String(row.id)}`, { method: "DELETE" });
      setEditing(null);
      setRefresh((value) => value + 1);
      onSaved();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to delete category.",
      );
    }
  }
  return (
    <Dialog
      title="Manage SKU categories"
      eyebrow="Service catalog"
      onClose={onClose}
    >
      {loading ? (
        <Loading />
      ) : loadError ? (
        <ErrorNotice message={loadError} />
      ) : (
        <div className="category-manager">
          <form
            className="admin-form category-form"
            key={String(editing?.id || "new")}
            onSubmit={submit}
          >
            <div className="admin-field-grid">
              <label>
                Category name *
                <input
                  name="name"
                  required
                  maxLength={100}
                  autoFocus
                  defaultValue={String(editing?.name || "")}
                />
              </label>
              <label>
                Sort order
                <input
                  name="sortOrder"
                  type="number"
                  min="0"
                  step="1"
                  defaultValue={Number(editing?.sort_order || 0)}
                />
              </label>
            </div>
            <label>
              Description
              <textarea
                name="description"
                rows={2}
                maxLength={500}
                defaultValue={String(editing?.description || "")}
              />
            </label>
            {error && <p className="admin-form-error">{error}</p>}
            <div className="admin-form-actions">
              {editing && (
                <button
                  type="button"
                  className="admin-button secondary"
                  onClick={() => setEditing(null)}
                >
                  Cancel edit
                </button>
              )}
              <button className="admin-button primary" disabled={saving}>
                {saving
                  ? "Saving…"
                  : editing
                    ? "Save category"
                    : "Add category"}
              </button>
            </div>
          </form>
          <div className="category-list">
            {data?.categories.map((row) => (
              <div key={String(row.id)}>
                <span>
                  <strong>{String(row.name)}</strong>
                  <small>
                    {Number(row.sku_count)} SKU
                    {Number(row.sku_count) === 1 ? "" : "s"}
                    {row.description ? ` · ${String(row.description)}` : ""}
                  </small>
                </span>
                <div className="row-actions">
                  <button
                    type="button"
                    onClick={() => setEditing(row)}
                    title="Edit category"
                  >
                    <Pencil />
                  </button>
                  <button
                    type="button"
                    className="danger"
                    disabled={Number(row.sku_count) > 0}
                    onClick={() => remove(row)}
                    title={
                      Number(row.sku_count) > 0
                        ? "Move every SKU out of this category before deleting it"
                        : "Delete category"
                    }
                  >
                    <Trash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Dialog>
  );
}

function SkuTemplateManagerDialog({ onClose }: { onClose: () => void }) {
  const [refresh, setRefresh] = useState(0);
  const { data, error: loadError, loading } = useApi<{ templates: RecordRow[] }>("sku-templates", refresh);
  const [editing, setEditing] = useState<RecordRow | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true); setError("");
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      await api(editing ? `sku-templates/${String(editing.id)}` : "sku-templates", {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify({ name: form.get("name"), description: form.get("description") }),
      });
      setEditing(null); setRefresh((value) => value + 1); formElement.reset();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to save SKU template."); }
    finally { setSaving(false); }
  }
  async function remove(row: RecordRow) {
    if (!window.confirm(`Delete SKU template “${String(row.name)}”?`)) return;
    setError("");
    try { await api(`sku-templates/${String(row.id)}`, { method: "DELETE" }); setEditing(null); setRefresh((value) => value + 1); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to delete SKU template."); }
  }
  return <Dialog title="Manage SKU templates" eyebrow="Service catalog" onClose={onClose}>
    {loading ? <Loading /> : loadError ? <ErrorNotice message={loadError} /> : <div className="category-manager">
      <form className="admin-form category-form" key={String(editing?.id || "new")} onSubmit={submit}>
        <label>Template name *<input name="name" required maxLength={160} autoFocus defaultValue={String(editing?.name || "")} /></label>
        <label>Description *<textarea name="description" rows={6} required maxLength={1000} defaultValue={String(editing?.description || "")} /></label>
        {error && <p className="admin-form-error">{error}</p>}
        <div className="admin-form-actions">
          {editing && <button type="button" className="admin-button secondary" onClick={() => setEditing(null)}>Cancel edit</button>}
          <button className="admin-button primary" disabled={saving}>{saving ? "Saving…" : editing ? "Save template" : "Add template"}</button>
        </div>
      </form>
      <div className="category-list sku-template-list">
        {(data?.templates || []).map((row) => <div key={String(row.id)}><span><strong>{String(row.name)}</strong><small>{String(row.description)}</small></span><div className="row-actions"><button type="button" onClick={() => setEditing(row)} title="Edit template"><Pencil /></button><button type="button" className="danger" onClick={() => remove(row)} title="Delete template"><Trash2 /></button></div></div>)}
        {!data?.templates.length && <div className="empty-cell">No SKU templates have been created.</div>}
      </div>
    </div>}
  </Dialog>;
}

function ApplySkuTemplateDialog({ skus, onClose, onSaved }: { skus: RecordRow[]; onClose: () => void; onSaved: () => void }) {
  const { data, error: loadError, loading } = useApi<{ templates: RecordRow[] }>("sku-templates");
  const [templateId, setTemplateId] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  async function apply() {
    if (!templateId) { setError("Select a SKU template."); return; }
    setSaving(true); setError("");
    try {
      await api(`sku-templates/${templateId}/apply`, { method: "POST", body: JSON.stringify({ skuIds: skus.map((sku) => Number(sku.id)) }) });
      onSaved(); onClose();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to apply SKU template."); }
    finally { setSaving(false); }
  }
  return <Dialog title="Apply SKU template" eyebrow={`${skus.length} selected SKU${skus.length === 1 ? "" : "s"}`} onClose={onClose}>
    {loading ? <Loading /> : loadError ? <ErrorNotice message={loadError} /> : <div className="sku-apply-template">
      <label>SKU template<select value={templateId} onChange={(event) => setTemplateId(event.target.value)}><option value="">Select template</option>{(data?.templates || []).map((row) => <option key={String(row.id)} value={String(row.id)}>{String(row.name)}</option>)}</select></label>
      <div className="sku-apply-list">{skus.map((sku) => <span key={String(sku.id)}><strong>{String(sku.sku)}</strong>{String(sku.name)}</span>)}</div>
      {error && <p className="admin-form-error">{error}</p>}
      <div className="admin-form-actions"><button type="button" className="admin-button secondary" onClick={onClose}>Cancel</button><button type="button" className="admin-button primary" disabled={saving || !data?.templates.length} onClick={apply}>{saving ? "Applying…" : "Apply template"}</button></div>
    </div>}
  </Dialog>;
}

function SkuHistoryDialog({
  sku,
  onClose,
}: {
  sku: RecordRow;
  onClose: () => void;
}) {
  const { data, error, loading } = useApi<{ revisions: RecordRow[] }>(
    `skus/${String(sku.id)}/revisions`,
  );
  return (
    <Dialog
      title={`${String(sku.sku)} revision history`}
      eyebrow="Backend catalog history"
      onClose={onClose}
    >
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorNotice message={error} />
      ) : (
        <div className="revision-history">
          <p className="admin-form-note">
            The highest revision ID is current. Administrators may correct that current revision globally or create
            a new revision for an intentional catalog change.
          </p>
          {(data?.revisions || []).map((revision, index) => (
            <article
              key={String(revision.revision_key)}
              className={index === 0 ? "current" : ""}
            >
              <div>
                <strong>
                  {index === 0 ? "Current revision" : "Prior revision"}
                </strong>
                <code>{String(revision.revision_id)}</code>
              </div>
              <span>
                {String(revision.name)} · {currency(revision.unit_price_cents)}{" "}
                {String(revision.unit_label)}
              </span>
              <small>
                {String(revision.change_summary || "No revision summary")} ·{" "}
                {String(revision.created_by || "system")} ·{" "}
                {String(revision.created_at)}
              </small>
            </article>
          ))}
        </div>
      )}
    </Dialog>
  );
}

function SkuUsageDialog({ sku, onClose }: { sku: RecordRow; onClose: () => void }) {
  const customers = String(sku.usage_customers || "").split("||").filter(Boolean);
  return (
    <Dialog
      title={`Customers using ${String(sku.sku)}`}
      eyebrow="SKU assignments"
      onClose={onClose}
    >
      <div className="sku-usage-dialog">
        <p>
          Current customer assignments for <strong>{String(sku.name)}</strong>. Existing contracted services remain associated with this SKU if it is inactivated.
        </p>
        {customers.length ? (
          <div className="sku-usage-list">
            {customers.map((customer) => <div key={customer}><Users /><span>{customer}</span></div>)}
          </div>
        ) : (
          <div className="empty-cell">No active customer assignments use this SKU.</div>
        )}
        <div className="admin-form-actions">
          <button type="button" className="admin-button secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </Dialog>
  );
}

function PaymentDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const invoices = useApi<{ invoices: RecordRow[] }>("invoices");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api("payments", {
        method: "POST",
        body: JSON.stringify({
          invoiceId: form.get("invoiceId"),
          amountCents: Math.round(Number(form.get("amount")) * 100),
          receivedDate: form.get("receivedDate"),
          method: form.get("method"),
          reference: form.get("reference"),
          notes: form.get("notes"),
        }),
      });
      onSaved();
      onClose();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Unable to record payment.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <Dialog title="Record payment" eyebrow="Payments" onClose={onClose}>
      {invoices.loading ? (
        <Loading />
      ) : invoices.error ? (
        <ErrorNotice message={invoices.error} />
      ) : (
        <form className="admin-form" onSubmit={submit}>
          <div className="admin-field-grid">
            <label>
              Open invoice *
              <select name="invoiceId" required defaultValue="">
                <option value="" disabled>
                  Select invoice
                </option>
                {invoices.data?.invoices
                  .filter(
                    (row) => !["paid", "void"].includes(String(row.status)),
                  )
                  .map((row) => (
                    <option key={String(row.id)} value={String(row.id)}>
                      {String(row.invoice_number)} · {String(row.customer)} ·{" "}
                      {currency(row.total_cents)}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Amount received *
              <input
                name="amount"
                type="number"
                min="0.01"
                step="0.01"
                required
              />
            </label>
            <label>
              Received date *
              <input
                name="receivedDate"
                type="date"
                defaultValue={new Date().toISOString().slice(0, 10)}
                required
              />
            </label>
            <label>
              Method
              <select name="method">
                <option value="check">Check</option>
                <option value="ach">ACH</option>
                <option value="cash">Cash</option>
                <option value="card_provider">Card provider</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label>
              Reference
              <input name="reference" maxLength={120} />
            </label>
          </div>
          <label>
            Notes
            <textarea name="notes" rows={3} maxLength={1000} />
          </label>
          {error && <p className="admin-form-error">{error}</p>}
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-button secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button className="admin-button primary" disabled={saving}>
              {saving ? "Saving…" : "Record payment"}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

function AdminSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<{
    customers: RecordRow[];
    invoices: RecordRow[];
    skus: RecordRow[];
  } | null>(null);
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults(null);
      return;
    }
    let active = true;
    const timer = window.setTimeout(
      () =>
        api<{
          customers: RecordRow[];
          invoices: RecordRow[];
          skus: RecordRow[];
        }>(`search?q=${encodeURIComponent(query.trim())}`)
          .then((value) => {
            if (active) setResults(value);
          })
          .catch(() => {
            if (active) setResults(null);
          }),
      220,
    );
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query]);
  const groups = results
    ? ([
        ["Customers", results.customers, "/admin/customers"],
        ["Invoices", results.invoices, "/admin/invoices"],
        ["SKUs", results.skus, "/admin/catalog"],
      ] as const)
    : [];
  const count = groups.reduce((sum, group) => sum + group[1].length, 0);
  return (
    <div className="admin-search-wrap">
      <div className="admin-search">
        <Search />
        <input
          aria-label="Search customers, invoices, and SKUs"
          placeholder="Search customers, invoices, SKUs"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
          >
            <X />
          </button>
        )}
      </div>
      {results && (
        <div className="admin-search-results">
          {count ? (
            groups.map(([label, rows, path]) =>
              rows.length ? (
                <section key={label}>
                  <strong>{label}</strong>
                  {rows.map((row) => (
                    <Link
                      key={`${label}-${String(row.id)}`}
                      to={path}
                      onClick={() => setQuery("")}
                    >
                      <span>{String(row.label)}</span>
                      <small>{String(row.detail || "")}</small>
                    </Link>
                  ))}
                </section>
              ) : null,
            )
          ) : (
            <p>No matching records.</p>
          )}
        </div>
      )}
    </div>
  );
}

function AdminShell({
  children,
  onNewInvoice,
}: {
  children: ReactNode;
  onNewInvoice: () => void;
}) {
  const location = useLocation();
  const [menu, setMenu] = useState(false);
  const [email, setEmail] = useState("Administrator");
  useEffect(() => {
    api<{ email: string }>("session")
      .then((session) => setEmail(session.email))
      .catch(() => undefined);
  }, []);
  useEffect(() => setMenu(false), [location.pathname]);
  const active = navItems.find(([path, , , exact]) =>
    exact ? location.pathname === path : location.pathname.startsWith(path),
  );
  return (
    <div className="admin-root">
      <aside className={menu ? "admin-sidebar open" : "admin-sidebar"}>
        <div className="admin-brand">
          <img src="/logo-horizontal.png" alt="Eaton Consulting" />
          <span>Admin Portal</span>
        </div>
        <nav aria-label="Administrative navigation">
          {navItems.map(([path, label, Icon, exact]) => (
            <NavLink key={path} to={path} end={exact}>
              <Icon />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="admin-secure">
          <LockKeyhole />
          <span>
            <strong>Secure session</strong>
            <small>Cloudflare Access</small>
          </span>
        </div>
      </aside>
      <div className="admin-workspace">
        <header className="admin-topbar">
          <button
            className="admin-menu"
            type="button"
            onClick={() => setMenu(!menu)}
            aria-label="Toggle menu"
          >
            <Menu />
          </button>
          <h1>{active?.[1] || "Admin Portal"}</h1>
          <span className="environment">
            <i /> Production
          </span>
          <AdminSearch />
          <button
            className="admin-bell"
            type="button"
            aria-label="Notifications"
          >
            <Bell />
          </button>
          <div className="admin-user">
            <span>{email.slice(0, 1).toUpperCase()}</span>
            <strong>{email}</strong>
          </div>
          <button
            className="admin-button primary top-invoice"
            type="button"
            onClick={onNewInvoice}
          >
            <FilePlus2 /> New Invoice
          </button>
        </header>
        <main className="admin-main">{children}</main>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
  tone = "blue",
}: {
  label: string;
  value: string;
  icon: typeof CircleDollarSign;
  tone?: string;
}) {
  return (
    <article className="admin-metric">
      <span className={`metric-icon ${tone}`}>
        <Icon />
      </span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    </article>
  );
}

function RevenueChart({ data }: { data: DashboardData["revenue"] }) {
  const max = Math.max(
    100,
    ...data.flatMap((row) => [row.invoicedCents, row.paidCents]),
  );
  const points = (key: "invoicedCents" | "paidCents") =>
    data
      .map(
        (row, index) => `${40 + index * 104},${155 - (row[key] / max) * 125}`,
      )
      .join(" ");
  return (
    <div className="revenue-chart">
      <svg
        viewBox="0 0 600 180"
        role="img"
        aria-label="Invoiced and paid revenue over the last six months"
      >
        <defs>
          <linearGradient id="revenue-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#087cff" stopOpacity=".32" />
            <stop offset="1" stopColor="#087cff" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[30, 72, 114, 155].map((y) => (
          <line key={y} x1="35" x2="570" y1={y} y2={y} />
        ))}
        <polygon
          points={`40,155 ${points("invoicedCents")} 560,155`}
          fill="url(#revenue-fill)"
          stroke="none"
        />
        <polyline points={points("invoicedCents")} className="invoiced-line" />
        <polyline points={points("paidCents")} className="paid-line" />
        {data.map((row, index) => (
          <g key={row.month}>
            <circle
              cx={40 + index * 104}
              cy={155 - (row.invoicedCents / max) * 125}
              r="4"
              className="invoiced-dot"
            />
            <circle
              cx={40 + index * 104}
              cy={155 - (row.paidCents / max) * 125}
              r="4"
              className="paid-dot"
            />
            <text x={40 + index * 104} y="176">
              {new Date(`${row.month}-02T12:00:00`).toLocaleDateString(
                "en-US",
                { month: "short" },
              )}
            </text>
          </g>
        ))}
      </svg>
      <div className="chart-legend">
        <span>
          <i className="invoiced" /> Invoiced
        </span>
        <span>
          <i className="paid" /> Paid
        </span>
      </div>
    </div>
  );
}

function Dashboard({
  onNewInvoice,
  onAddCustomer,
  onAddSku,
}: {
  onNewInvoice: () => void;
  onAddCustomer: () => void;
  onAddSku: () => void;
}) {
  const { data, error, loading } = useApi<DashboardData>("dashboard");
  if (loading) return <Loading />;
  if (error || !data)
    return <ErrorNotice message={error || "Dashboard data is unavailable."} />;
  const statuses = ["draft", "sent", "overdue", "paid"];
  const total = statuses.reduce(
    (sum, status) => sum + (data.statusCounts[status] || 0),
    0,
  );
  const paidDegrees = total ? ((data.statusCounts.paid || 0) / total) * 360 : 0;
  const sentDegrees = total ? ((data.statusCounts.sent || 0) / total) * 360 : 0;
  const overdueDegrees = total
    ? ((data.statusCounts.overdue || 0) / total) * 360
    : 0;
  return (
    <>
      <div className="admin-heading">
        <div>
          <h2>Business overview</h2>
          <p>Billing, customers, and managed services at a glance.</p>
        </div>
      </div>
      <div className="dashboard-layout">
        <div className="dashboard-core">
          <section className="metrics-grid">
            <MetricCard
              label="Outstanding"
              value={currency(data.metrics.outstandingCents)}
              icon={CircleDollarSign}
            />
            <MetricCard
              label="Overdue"
              value={currency(data.metrics.overdueCents)}
              icon={Clock3}
              tone="red"
            />
            <MetricCard
              label="Paid this month"
              value={currency(data.metrics.paidThisMonthCents)}
              icon={BarChart3}
              tone="cyan"
            />
            <MetricCard
              label="Active customers"
              value={String(data.metrics.activeCustomers)}
              icon={Users}
              tone="green"
            />
          </section>
          <div className="dashboard-charts">
            <section className="admin-panel">
              <header>
                <h3>Revenue & payments</h3>
                <span>Last 6 months</span>
              </header>
              <RevenueChart data={data.revenue} />
            </section>
            <section className="admin-panel invoice-status-panel">
              <header>
                <h3>Invoice status</h3>
              </header>
              <div className="status-chart">
                <div
                  className="donut"
                  style={{
                    background: total
                      ? `conic-gradient(#55c978 0 ${paidDegrees}deg, #1480ff ${paidDegrees}deg ${paidDegrees + sentDegrees}deg, #ff7148 ${paidDegrees + sentDegrees}deg ${paidDegrees + sentDegrees + overdueDegrees}deg, #8b9aae ${paidDegrees + sentDegrees + overdueDegrees}deg 360deg)`
                      : "#1a2b3d",
                  }}
                >
                  <span />
                </div>
                <div className="status-legend">
                  {statuses.map((status) => (
                    <div key={status}>
                      <i className={status} />
                      <span>{titleCase(status)}</span>
                      <strong>{data.statusCounts[status] || 0}</strong>
                    </div>
                  ))}
                  <div className="status-total">
                    <span>Total</span>
                    <strong>{total}</strong>
                  </div>
                </div>
              </div>
            </section>
          </div>
          <section className="admin-panel recent-panel">
            <header>
              <h3>Recent invoices</h3>
              <NavLink to="/admin/invoices">
                View all invoices <ChevronRight />
              </NavLink>
            </header>
            <div className="admin-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Customer</th>
                    <th>Issued</th>
                    <th>Due</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentInvoices.length ? (
                    data.recentInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="link-cell">{invoice.invoice_number}</td>
                        <td>{invoice.customer}</td>
                        <td>{shortDate(invoice.issue_date)}</td>
                        <td>{shortDate(invoice.due_date)}</td>
                        <td>{currency(invoice.total_cents)}</td>
                        <td>
                          <Status value={invoice.status} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="empty-cell">
                        No invoices yet. Create the first draft when you are
                        ready.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
        <aside className="dashboard-rail">
          <section className="admin-panel quick-actions">
            <header>
              <h3>Quick actions</h3>
            </header>
            <button onClick={onNewInvoice}>
              <FilePlus2 />
              <span>Create invoice</span>
              <ChevronRight />
            </button>
            <button onClick={onAddCustomer}>
              <UserPlus />
              <span>Add customer</span>
              <ChevronRight />
            </button>
            <button onClick={onAddSku}>
              <Boxes />
              <span>Add SKU</span>
              <ChevronRight />
            </button>
            <NavLink to="/admin/packages">
              <PackagePlus />
              <span>Build package</span>
              <ChevronRight />
            </NavLink>
          </section>
          <section className="admin-panel security-card">
            <header>
              <h3>Security status</h3>
            </header>
            <div className="protected">
              <span>
                <ShieldCheck />
              </span>
              <strong>
                <i /> Protected
              </strong>
            </div>
            <p>
              <ShieldCheck /> Cloudflare Access JWT verified
            </p>
            <p>
              <Check /> Administrator allowlist enforced
            </p>
            <p>
              <LockKeyhole /> Database isolated from browser
            </p>
            <div className="last-signin">
              <small>Signed in as</small>
              <strong>{data.identity.email}</strong>
            </div>
          </section>
        </aside>
      </div>
    </>
  );
}

function PageHeader({
  title,
  copy,
  action,
}: {
  title: string;
  copy: string;
  action?: ReactNode;
}) {
  return (
    <div className="admin-heading page">
      <div>
        <h2>{title}</h2>
        <p>{copy}</p>
      </div>
      {action}
    </div>
  );
}

function RowActions({
  onEdit,
  onClone,
  onToggle,
  onDelete,
  active,
  kind,
}: {
  onEdit: () => void;
  onClone?: () => void;
  onToggle?: () => void;
  onDelete?: () => void;
  active?: boolean;
  kind: string;
}) {
  return (
    <div className="row-actions">
      {onClone && (
        <button type="button" onClick={onClone} title={`Clone ${kind}`}>
          <Copy />
        </button>
      )}
      <button type="button" onClick={onEdit} title={`Edit ${kind}`}>
        <Pencil />
      </button>
      {onToggle && (
        <button
          type="button"
          onClick={onToggle}
          title={`${active ? "Inactivate" : "Activate"} ${kind}`}
        >
          <Power />
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          className="danger"
          onClick={onDelete}
          title={`Delete ${kind}`}
        >
          <Trash2 />
        </button>
      )}
    </div>
  );
}

function CustomersPage({
  refresh,
  onAdd,
  onEdit,
}: {
  refresh: number;
  onAdd: () => void;
  onEdit: (row: RecordRow) => void;
}) {
  const { data, error, loading } = useApi<{ customers: RecordRow[] }>(
    "customers",
    refresh,
  );
  return (
    <>
      <PageHeader
        title="Customers"
        copy="Businesses, contacts, addresses, billing terms, sales tax, and account status."
        action={
          <button className="admin-button primary" onClick={onAdd}>
            <UserPlus /> Add customer
          </button>
        }
      />
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorNotice message={error} />
      ) : (
        <TablePanel
          headers={[
            "Customer #",
            "Company",
            "Contact",
            "Billing email",
            "Phone",
            "Address",
            "Terms",
            "Sales tax",
            "Status",
            "Actions",
          ]}
          rows={(data?.customers || []).map((row) => [
            String(row.customer_number || "—"),
            String(row.company_name),
            String(row.contact_name || "—"),
            String(row.billing_email),
            String(row.phone || "—"),
            <span className="address-cell">
              {String(row.billing_address || "—")}
            </span>,
            Number(row.payment_terms_days) === 0
              ? "Due on receipt"
              : `Net ${row.payment_terms_days}`,
            Number(row.tax_exempt) === 1 ? (
              <Status value="tax exempt" />
            ) : (
              `${(Number(row.sales_tax_rate_bps || 0) / 100).toFixed(2)}%`
            ),
            <Status value={row.status} />,
            <RowActions kind="customer" onEdit={() => onEdit(row)} />,
          ])}
          empty="No customers have been added."
        />
      )}
    </>
  );
}

function InvoicesPage({
  refresh,
  onAdd,
  onEdit,
  onChanged,
}: {
  refresh: number;
  onAdd: () => void;
  onEdit: (row: RecordRow) => void;
  onChanged: () => void;
}) {
  const { data, error, loading } = useApi<{ invoices: RecordRow[] }>(
    "invoices",
    refresh,
  );
  const [mutationError, setMutationError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  async function invoiceAction(
    row: RecordRow,
    action: "email" | "postal" | "cancel" | "delete",
  ) {
    if (
      (action === "postal" &&
        !window.confirm(
          `Confirm ${String(row.invoice_number)} was sent by postal mail?`,
        )) ||
      (action === "cancel" &&
        !window.confirm(
          `Cancel ${String(row.invoice_number)}? Its audit history will be retained.`,
        )) ||
      (action === "delete" &&
        !window.confirm(
          `Permanently delete draft ${String(row.invoice_number)}?`,
        ))
    )
      return;
    setBusy(`${row.id}-${action}`);
    setMutationError("");
    setNotice("");
    try {
      const result = await api<{ message: string }>(
        `invoices/${String(row.id)}${action === "delete" ? "" : `/${action}`}`,
        {
          method: action === "delete" ? "DELETE" : "POST",
          body: action === "delete" ? undefined : "{}",
        },
      );
      setNotice(result.message);
      onChanged();
    } catch (reason) {
      setMutationError(
        reason instanceof Error ? reason.message : "Unable to update invoice.",
      );
    } finally {
      setBusy("");
    }
  }
  async function bulk(mode: "all" | "never_sent") {
    const label =
      mode === "all"
        ? "every approved, unpaid invoice, including ones already emailed"
        : "approved, unpaid invoices that have never been emailed";
    if (!window.confirm(`Email ${label}?`)) return;
    setBusy(`bulk-${mode}`);
    setMutationError("");
    setNotice("");
    try {
      const result = await api<{ message: string }>("invoices/bulk-email", {
        method: "POST",
        body: JSON.stringify({ mode }),
      });
      setNotice(result.message);
      onChanged();
    } catch (reason) {
      setMutationError(
        reason instanceof Error ? reason.message : "Unable to send invoices.",
      );
    } finally {
      setBusy("");
    }
  }
  async function duplicate(row: RecordRow) {
    setBusy(`${row.id}-duplicate`);
    setMutationError("");
    setNotice("");
    try {
      const result = await api<{ message: string }>(
        `invoices/${String(row.id)}/duplicate`,
        { method: "POST", body: "{}" },
      );
      setNotice(result.message);
      onChanged();
    } catch (reason) {
      setMutationError(
        reason instanceof Error
          ? reason.message
          : "Unable to duplicate invoice.",
      );
    } finally {
      setBusy("");
    }
  }
  const actions = (
    <div className="heading-actions invoice-bulk-actions">
      <button
        className="admin-button secondary"
        disabled={Boolean(busy)}
        onClick={() => bulk("never_sent")}
      >
        <MailCheck /> Send never emailed
      </button>
      <button
        className="admin-button secondary"
        disabled={Boolean(busy)}
        onClick={() => bulk("all")}
      >
        <Send /> Send all approved & unpaid
      </button>
      <button className="admin-button primary" onClick={onAdd}>
        <FilePlus2 /> New invoice
      </button>
    </div>
  );
  return (
    <>
      <PageHeader
        title="Invoices"
        copy="Edit, approve, deliver, download, cancel, and retain professional customer invoices."
        action={actions}
      />
      {notice && <p className="admin-form-success page-error">{notice}</p>}
      {mutationError && (
        <p className="admin-form-error page-error">{mutationError}</p>
      )}
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorNotice message={error} />
      ) : (
        <TablePanel
          headers={[
            "Invoice",
            "Customer",
            "Issued",
            "Due",
            "Amount",
            "Approval",
            "Delivery",
            "Last notified",
            "Actions",
          ]}
          rows={(data?.invoices || []).map((row) => {
            const unpaidApproved =
              row.approval_status === "approved" &&
              row.status !== "void" &&
              (Number(row.correction_pending_resend) === 1 ||
                (!["paid", "partial"].includes(String(row.status)) && Number(row.paid_cents) === 0));
            const deletable =
              row.status === "draft" &&
              row.approval_status === "draft";
            return [
              <button
                className="table-link-button"
                type="button"
                onClick={() => onEdit(row)}
              >
                {String(row.invoice_number)}
              </button>,
              String(row.customer),
              shortDate(row.issue_date),
              shortDate(row.due_date),
              currency(row.total_cents),
              <Status value={row.approval_status} />,
              Number(row.correction_pending_resend) === 1
                ? <Status value="corrected · resend" />
                : <Status value={invoiceDisplayStatus(row.status)} />,
              row.last_customer_notified_at
                ? String(row.last_customer_notified_at)
                : "Never",
              <div className="row-actions invoice-row-actions">
                <button
                  type="button"
                  onClick={() => onEdit(row)}
                  title="Open or edit invoice"
                >
                  <Pencil />
                </button>
                <a href={invoicePdfUrl(row.id)} title="Download PDF">
                  <Download />
                </a>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => duplicate(row)}
                  title="Duplicate charges and historical pricing as a new draft"
                >
                  <Copy />
                </button>
                <button
                  type="button"
                  disabled={!unpaidApproved || Boolean(busy)}
                  onClick={() => invoiceAction(row, "email")}
                  title={
                    row.last_emailed_at
                      ? `Email PDF again · last sent ${String(row.last_emailed_at)}`
                      : "Email PDF"
                  }
                >
                  <Mail />
                </button>
                <button
                  type="button"
                  disabled={!unpaidApproved || Boolean(busy)}
                  onClick={() => invoiceAction(row, "postal")}
                  title="Mark sent by postal mail"
                >
                  <Stamp />
                </button>
                <button
                  type="button"
                  className="danger"
                  disabled={
                    row.status === "void" ||
                    Number(row.paid_cents) > 0 ||
                    Boolean(busy)
                  }
                  onClick={() => invoiceAction(row, "cancel")}
                  title="Cancel invoice"
                >
                  <Ban />
                </button>
                <button
                  type="button"
                  className="danger"
                  disabled={!deletable || Boolean(busy)}
                  onClick={() => invoiceAction(row, "delete")}
                  title="Permanently delete draft"
                >
                  <Trash2 />
                </button>
              </div>,
            ];
          })}
          empty="No invoices have been created."
        />
      )}
    </>
  );
}

function CatalogPage({
  refresh,
  onAdd,
  onEdit,
  onClone,
  onUsage,
  onHistory,
  onCategories,
  onTemplates,
  onApplyTemplate,
  onChanged,
}: {
  refresh: number;
  onAdd: () => void;
  onEdit: (row: RecordRow) => void;
  onClone: (row: RecordRow) => void;
  onUsage: (row: RecordRow) => void;
  onHistory: (row: RecordRow) => void;
  onCategories: () => void;
  onTemplates: () => void;
  onApplyTemplate: (rows: RecordRow[]) => void;
  onChanged: () => void;
}) {
  const { data, error, loading } = useApi<{ skus: RecordRow[] }>(
    "skus",
    refresh,
  );
  const [statusFilter, setStatusFilter] = useState<"active" | "all" | "inactive">("active");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [mutationError, setMutationError] = useState("");
  const [mutationMessage, setMutationMessage] = useState("");
  const [selectedSkuIds, setSelectedSkuIds] = useState<Set<string>>(new Set());
  useEffect(() => setSelectedSkuIds(new Set()), [refresh]);
  const categoryOptions = Array.from(
    new Map((data?.skus || []).map((row) => [String(row.category_id), String(row.category)])).entries(),
  ).sort((left, right) => left[1].localeCompare(right[1]));
  function toggle(row: RecordRow) {
    onEdit({ ...row, active: Number(row.active) === 1 ? 0 : 1 });
  }
  async function remove(row: RecordRow) {
    const customers = String(row.usage_customers || "").split("||").filter(Boolean);
    const historicalDependencyCount = Number(row.invoice_count || 0) + Number(row.contract_count || 0);
    const packageCount = Number(row.package_count || 0);
    const inUse = historicalDependencyCount > 0;
    const prompt = inUse
      ? [
          `${String(row.sku)} is in use and cannot be permanently deleted. It will be inactivated instead.`,
          customers.length ? `\nCustomers currently assigned this SKU:\n${customers.map((customer) => `• ${customer}`).join("\n")}` : "\nNo active customer assignments were found; package or historical billing references require this SKU to be retained.",
          "\nExisting contracted-service and invoice history will remain intact.",
        ].join("\n")
      : packageCount > 0
        ? `Permanently delete unused SKU ${String(row.sku)}? It will also be removed from ${packageCount} package configuration${packageCount === 1 ? "" : "s"}.`
        : `Permanently delete unused SKU ${String(row.sku)}?`;
    if (
      !window.confirm(prompt)
    )
      return;
    setMutationError("");
    setMutationMessage("");
    try {
      const result = await api<{ message: string }>(`skus/${String(row.id)}`, { method: "DELETE" });
      setMutationMessage(result.message);
      onChanged();
    } catch (reason) {
      setMutationError(
        reason instanceof Error ? reason.message : "Unable to delete SKU.",
      );
    }
  }
  return (
    <>
      <PageHeader
        title="SKU Catalog"
        copy="The authoritative catalog for managed, project, hourly, and other billable services."
        action={
          <div className="heading-actions">
            <label className="catalog-filter">
              Status
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as "active" | "all" | "inactive")}>
                <option value="active">Active SKUs</option>
                <option value="all">All SKUs</option>
                <option value="inactive">Inactive SKUs</option>
              </select>
            </label>
            <label className="catalog-filter">
              Scope
              <select value={scopeFilter} onChange={(event) => setScopeFilter(event.target.value)}>
                <option value="all">All scopes</option>
                <option value="managed">Managed service</option>
                <option value="project">Project</option>
                <option value="hourly">Hourly</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="catalog-filter">
              Category
              <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
                <option value="all">All categories</option>
                {categoryOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </label>
            <button className="admin-button secondary" onClick={onCategories}>
              <Tags /> Manage categories
            </button>
            <button className="admin-button secondary" onClick={onTemplates}>
              <Stamp /> Manage templates
            </button>
            <button className="admin-button secondary" disabled={!selectedSkuIds.size} onClick={() => onApplyTemplate((data?.skus || []).filter((row) => selectedSkuIds.has(String(row.id))))}>
              <Check /> Apply template ({selectedSkuIds.size})
            </button>
            <button className="admin-button primary" onClick={onAdd}>
              <Plus /> Add SKU
            </button>
          </div>
        }
      />
      {mutationError && (
        <p className="admin-form-error page-error">{mutationError}</p>
      )}
      {mutationMessage && <p className="admin-form-success page-message">{mutationMessage}</p>}
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorNotice message={error} />
      ) : (
        <TablePanel
          headers={[
            "Select",
            "SKU",
            "Service",
            "Category",
            "Scope",
            "Price",
            "Current revision",
            "Public",
            "Status",
            "Actions",
          ]}
          rows={(data?.skus || []).filter((row) =>
            (statusFilter === "all" || (statusFilter === "active" ? Number(row.active) === 1 : Number(row.active) === 0)) &&
            (scopeFilter === "all" || String(row.service_scope) === scopeFilter) &&
            (categoryFilter === "all" || String(row.category_id) === categoryFilter)
          ).map((row) => {
            const customers = String(row.usage_customers || "").split("||").filter(Boolean);
            return [
            <input type="checkbox" className="sku-row-checkbox" aria-label={`Select SKU ${String(row.sku)}`} checked={selectedSkuIds.has(String(row.id))} onChange={(event) => setSelectedSkuIds((current) => { const next = new Set(current); if (event.target.checked) next.add(String(row.id)); else next.delete(String(row.id)); return next; })} />,
            <span className="link-cell">{String(row.sku)}</span>,
            String(row.name),
            String(row.category),
            titleCase(row.service_scope),
            `${currency(row.unit_price_cents)} ${row.unit_label}`,
            <button
              className="revision-link"
              type="button"
              onClick={() => onHistory(row)}
              title="View backend revision history"
            >
              {String(row.current_revision_id)}{" "}
              <small>({String(row.revision_count)})</small>
            </button>,
            Number(row.visible_managed_configurator) ? "Visible" : "Hidden",
            <Status value={Number(row.active) ? "active" : "inactive"} />,
            <div className="row-actions">
              <button
                type="button"
                className="sku-usage-button"
                onClick={() => onUsage(row)}
                title={`View customers using ${String(row.sku)}`}
                aria-label={`View ${customers.length} customer${customers.length === 1 ? "" : "s"} using SKU ${String(row.sku)}`}
              >
                <Users /><small>{customers.length}</small>
              </button>
              <button
                type="button"
                onClick={() => onClone(row)}
                title={`Clone SKU ${String(row.sku)}`}
                aria-label={`Clone SKU ${String(row.sku)}`}
              >
                <Copy />
              </button>
              <button
                type="button"
                onClick={() => onHistory(row)}
                title="Revision history"
              >
                <History />
              </button>
              <RowActions
                kind="SKU"
                active={Number(row.active) === 1}
                onEdit={() => onEdit(row)}
                onToggle={() => toggle(row)}
                onDelete={() => remove(row)}
              />
            </div>,
          ];})}
          empty="No SKUs match the selected display filters."
        />
      )}
    </>
  );
}

function PackagesPage({
  refresh,
  onAdd,
  onEdit,
  onClone,
  onChanged,
}: {
  refresh: number;
  onAdd: () => void;
  onEdit: (row: RecordRow) => void;
  onClone: (row: RecordRow) => void;
  onChanged: () => void;
}) {
  const { data, error, loading } = useApi<{ bundles: RecordRow[] }>(
    "bundles",
    refresh,
  );
  const [mutationError, setMutationError] = useState("");
  async function toggle(row: RecordRow) {
    setMutationError("");
    try {
      await api(`bundles/${String(row.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ active: Number(row.active) !== 1 }),
      });
      onChanged();
    } catch (reason) {
      setMutationError(
        reason instanceof Error
          ? reason.message
          : "Unable to change package status.",
      );
    }
  }
  async function remove(row: RecordRow) {
    if (!window.confirm(`Permanently delete package ${String(row.code)}?`))
      return;
    setMutationError("");
    try {
      await api(`bundles/${String(row.id)}`, { method: "DELETE" });
      onChanged();
    } catch (reason) {
      setMutationError(
        reason instanceof Error ? reason.message : "Unable to delete package.",
      );
    }
  }
  return (
    <>
      <PageHeader
        title="Packages"
        copy="Managed-services bundles published from the same D1 catalog used by the public pricing page."
        action={
          <button className="admin-button primary" onClick={onAdd}>
            <PackagePlus /> Build package
          </button>
        }
      />
      {mutationError && (
        <p className="admin-form-error page-error">{mutationError}</p>
      )}
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorNotice message={error} />
      ) : (
        <TablePanel
          headers={[
            "Code",
            "Package",
            "Items",
            "Pricing",
            "Public",
            "Status",
            "Actions",
          ]}
          rows={(data?.bundles || []).map((row) => [
            <span className="link-cell">{String(row.code)}</span>,
            String(row.name),
            String(row.item_count),
            row.pricing_mode === "fixed"
              ? currency(row.price_cents)
              : `${currency(row.calculated_price_cents)} · Calculated from SKUs`,
            Number(row.visible_managed_pricing) ? "Visible" : "Hidden",
            <Status value={Number(row.active) ? "active" : "inactive"} />,
            <RowActions
              kind="package"
              active={Number(row.active) === 1}
              onClone={() => onClone(row)}
              onEdit={() => onEdit(row)}
              onToggle={() => toggle(row)}
              onDelete={() => remove(row)}
            />,
          ])}
          empty="No managed-service packages exist."
        />
      )}
    </>
  );
}

function PaymentsPage({
  refresh,
  onAdd,
}: {
  refresh: number;
  onAdd: () => void;
}) {
  const { data, error, loading } = useApi<{ payments: RecordRow[] }>(
    "payments",
    refresh,
  );
  return (
    <>
      <PageHeader
        title="Payments"
        copy="Record and reconcile payments without storing card data."
        action={
          <button className="admin-button primary" onClick={onAdd}>
            <CircleDollarSign /> Record payment
          </button>
        }
      />
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorNotice message={error} />
      ) : (
        <TablePanel
          headers={[
            "Received",
            "Invoice",
            "Customer",
            "Amount",
            "Method",
            "Reference",
          ]}
          rows={(data?.payments || []).map((row) => [
            shortDate(row.received_date),
            <span className="link-cell">{String(row.invoice_number)}</span>,
            String(row.customer),
            currency(row.amount_cents),
            titleCase(row.method),
            String(row.reference || "—"),
          ])}
          empty="No payments have been recorded."
        />
      )}
    </>
  );
}

function ReportsPage() {
  return (
    <>
      <PageHeader
        title="Reports"
        copy="Billing and customer reporting generated from the business database."
      />
      <div className="report-grid">
        <article className="admin-panel">
          <BarChart3 />
          <h3>Revenue reporting</h3>
          <p>
            Monthly invoiced and collected totals are available on the dashboard
            now. Exportable period reports will build on the same immutable
            invoice records.
          </p>
        </article>
        <article className="admin-panel">
          <Building2 />
          <h3>Customer reporting</h3>
          <p>
            Review active customers, balances, payment history, and
            managed-service coverage without duplicating business data.
          </p>
        </article>
        <article className="admin-panel">
          <ReceiptText />
          <h3>Accounting exports</h3>
          <p>
            CSV exports will preserve invoice numbers, dates, taxes, line items,
            and payment references for accounting workflows.
          </p>
        </article>
      </div>
    </>
  );
}

function AuditPage() {
  const { data, error, loading } = useApi<{ events: RecordRow[] }>("audit");
  return (
    <>
      <PageHeader
        title="Audit Log"
        copy="Security-relevant administrative actions retained with actor and request context."
      />
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorNotice message={error} />
      ) : (
        <TablePanel
          headers={[
            "Time",
            "Administrator",
            "Action",
            "Entity",
            "Summary",
            "Source IP",
          ]}
          rows={(data?.events || []).map((row) => [
            String(row.created_at),
            String(row.actor_email),
            titleCase(row.action),
            titleCase(row.entity_type),
            String(row.summary),
            String(row.ip_address || "—"),
          ])}
          empty="No administrative changes have been recorded."
        />
      )}
    </>
  );
}

function escapeRegistryValue(value: string) {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function buildPuttyProtocolRegistry(path: string) {
  const powerShellPath = path.replace(/'/g, "''");
  const command = `powershell.exe -NoProfile -ExecutionPolicy Bypass -WindowStyle Hidden -Command "& { param([string]$rawUri); try { $uri = [System.Uri]$rawUri; $hostname = $uri.DnsSafeHost; if ($uri.Scheme -ne 'ssh' -or [string]::IsNullOrWhiteSpace($hostname) -or $hostname -notmatch '^[A-Za-z0-9.-]+$') { exit 1 }; Start-Process -FilePath '${powerShellPath}' -ArgumentList @('-ssh', $hostname, '-P', '22') } catch { exit 1 } }" "%1"`;
  return [
    "Windows Registry Editor Version 5.00",
    "",
    "; Eaton Consulting SSH handler for PuTTY",
    "; Applies only to the Windows user who runs this file.",
    "[HKEY_CURRENT_USER\\Software\\Classes\\ssh]",
    '@="URL:SSH Protocol"',
    '"URL Protocol"=""',
    "",
    "[HKEY_CURRENT_USER\\Software\\Classes\\ssh\\DefaultIcon]",
    `@="${escapeRegistryValue(`${path},0`)}"`,
    "",
    "[HKEY_CURRENT_USER\\Software\\Classes\\ssh\\shell\\open\\command]",
    `@="${escapeRegistryValue(command)}"`,
    "",
  ].join("\r\n");
}

function PuttySetupPage() {
  const [puttyPath, setPuttyPath] = useState("C:\\Program Files\\PuTTY\\putty.exe");
  const [error, setError] = useState("");

  function downloadRegistryFile() {
    const normalizedPath = puttyPath.trim();
    if (!/^[A-Za-z]:\\(?:[^<>:"|?*\\r\\n]+\\)*[^<>:"|?*\\r\\n]+\.exe$/i.test(normalizedPath)) {
      setError("Enter a full Windows path to putty.exe, for example C:\\Program Files\\PuTTY\\putty.exe.");
      return;
    }
    setError("");
    const file = new Blob([buildPuttyProtocolRegistry(normalizedPath)], { type: "application/x-ms-regedit" });
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = "EatonConsulting-SSH-PuTTY.reg";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <PageHeader
        title="SSH / PuTTY setup"
        copy="Create a one-time Windows registry setup file so device SSH links open directly in PuTTY."
      />
      <section className="admin-panel putty-setup-panel">
        <header>
          <Terminal />
          <div>
            <h3>Register SSH links for PuTTY</h3>
            <span>Configured locally on this Windows PC</span>
          </div>
        </header>
        <div className="putty-setup-content">
          <p>
            Enter the actual PuTTY executable path, download the registry file, and run it once. It applies to the currently signed-in Windows user and opens <code>ssh://hostname</code> with SSH on port 22.
          </p>
          <label>
            PuTTY executable path
            <input
              value={puttyPath}
              onChange={(event) => setPuttyPath(event.target.value)}
              spellCheck={false}
              placeholder="C:\\Program Files\\PuTTY\\putty.exe"
              aria-describedby="putty-path-help"
            />
          </label>
          <small id="putty-path-help">The common 64-bit location is <code>C:\\Program Files\\PuTTY\\putty.exe</code>. Select the file with Windows Explorer if you are unsure.</small>
          {error && <p className="admin-form-error">{error}</p>}
          <div className="admin-form-actions putty-setup-actions">
            <button className="admin-button primary" type="button" onClick={downloadRegistryFile}>
              <Download /> Download PuTTY SSH setup (.reg)
            </button>
          </div>
          <ol>
            <li>Download the <code>.reg</code> file.</li>
            <li>Double-click it and approve the Windows registry prompt.</li>
            <li>Return to a device and click <strong>SSH</strong>.</li>
          </ol>
        </div>
      </section>
    </>
  );
}

function SettingsPage() {
  const [refresh, setRefresh] = useState(0);
  const { data, error, loading } = useApi<{
    settings: RecordRow;
    emailDeliveryConfigured: boolean;
  }>("invoice-settings", refresh);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<{ message: string }>("invoice-settings", {
        method: "PUT",
        body: JSON.stringify({
          businessName: form.get("businessName"),
          businessAddress: form.get("businessAddress"),
          businessPhone: form.get("businessPhone"),
          businessEmail: form.get("businessEmail"),
          remitToName: form.get("remitToName"),
          remitToAddress: form.get("remitToAddress"),
          paymentInstructions: form.get("paymentInstructions"),
          invoiceFooter: form.get("invoiceFooter"),
          defaultSalesTaxRateBps: Math.round(
            Number(form.get("defaultSalesTaxRate")) * 100,
          ),
          emailSubjectTemplate: form.get("emailSubjectTemplate"),
          emailMessage: form.get("emailMessage"),
          replyToEmail: form.get("replyToEmail"),
        }),
      });
      setMessage(result.message);
      setRefresh((value) => value + 1);
    } catch (reason) {
      setMessage(
        reason instanceof Error ? reason.message : "Unable to save settings.",
      );
    } finally {
      setSaving(false);
    }
  }
  const settings = data?.settings;
  return (
    <>
      <PageHeader
        title="Settings"
        copy="Configure the business identity, remittance block, invoice defaults, and customer email."
      />
      {loading ? (
        <Loading />
      ) : error || !settings ? (
        <ErrorNotice message={error || "Invoice settings are unavailable."} />
      ) : (
        <>
          <div className="settings-grid settings-status">
            <section className="admin-panel setting-card">
              <ShieldCheck />
              <div>
                <h3>Cloudflare Access</h3>
                <p>JWT validation and administrator allowlist are active.</p>
              </div>
              <Status value="active" />
            </section>
            <section className="admin-panel setting-card">
              <Boxes />
              <div>
                <h3>D1 business database</h3>
                <p>Invoice settings and notification history are connected.</p>
              </div>
              <Status value="active" />
            </section>
            <section className="admin-panel setting-card">
              <Mail />
              <div>
                <h3>Invoice email delivery</h3>
                <p>
                  {data.emailDeliveryConfigured
                    ? "Resend is configured for PDF delivery."
                    : "Add the RESEND_API_KEY Worker secret before emailing invoices."}
                </p>
              </div>
              <Status
                value={data.emailDeliveryConfigured ? "active" : "inactive"}
              />
            </section>
          </div>
          <section className="admin-panel settings-form-panel">
            <header>
              <h3>Invoice & remittance settings</h3>
              <span>Used on generated PDFs and invoice emails</span>
            </header>
            <form className="admin-form settings-form" onSubmit={submit}>
              <h4>Business identity</h4>
              <div className="admin-field-grid">
                <label>
                  Business name *
                  <input
                    name="businessName"
                    required
                    maxLength={160}
                    defaultValue={String(settings.business_name || "")}
                  />
                </label>
                <label>
                  Business email
                  <input
                    name="businessEmail"
                    type="email"
                    maxLength={200}
                    defaultValue={String(settings.business_email || "")}
                  />
                </label>
                <label>
                  Business phone
                  <input
                    name="businessPhone"
                    maxLength={50}
                    defaultValue={String(settings.business_phone || "")}
                  />
                </label>
                <label>
                  Default Michigan sales tax (%)
                  <input
                    name="defaultSalesTaxRate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    required
                    defaultValue={
                      Number(settings.default_sales_tax_rate_bps || 0) / 100
                    }
                  />
                </label>
              </div>
              <label>
                Business address
                <textarea
                  name="businessAddress"
                  rows={3}
                  maxLength={1000}
                  defaultValue={String(settings.business_address || "")}
                />
              </label>
              <h4>Remit payment to</h4>
              <div className="admin-field-grid">
                <label>
                  Remit-to name *
                  <input
                    name="remitToName"
                    required
                    maxLength={160}
                    defaultValue={String(settings.remit_to_name || "")}
                  />
                </label>
                <label>
                  Reply-to email
                  <input
                    name="replyToEmail"
                    type="email"
                    maxLength={200}
                    defaultValue={String(settings.reply_to_email || "")}
                  />
                </label>
              </div>
              <label>
                Remit-to address
                <textarea
                  name="remitToAddress"
                  rows={4}
                  maxLength={1000}
                  placeholder="Street&#10;City, State ZIP"
                  defaultValue={String(settings.remit_to_address || "")}
                />
              </label>
              <label>
                Payment instructions
                <textarea
                  name="paymentInstructions"
                  rows={3}
                  maxLength={2000}
                  defaultValue={String(settings.payment_instructions || "")}
                />
              </label>
              <h4>Email & PDF wording</h4>
              <label>
                Email subject *
                <input
                  name="emailSubjectTemplate"
                  required
                  maxLength={300}
                  defaultValue={String(settings.email_subject_template || "")}
                />
                <small>
                  Use {"{{invoice_number}}"} and {"{{customer}}"} as
                  placeholders.
                </small>
              </label>
              <label>
                Email message *
                <textarea
                  name="emailMessage"
                  required
                  rows={4}
                  maxLength={3000}
                  defaultValue={String(settings.email_message || "")}
                />
              </label>
              <label>
                PDF footer
                <textarea
                  name="invoiceFooter"
                  rows={2}
                  maxLength={500}
                  defaultValue={String(settings.invoice_footer || "")}
                />
              </label>
              {message && (
                <p
                  className={
                    message.includes("saved")
                      ? "admin-form-success"
                      : "admin-form-error"
                  }
                >
                  {message}
                </p>
              )}
              <div className="admin-form-actions">
                <button className="admin-button primary" disabled={saving}>
                  <Save /> {saving ? "Saving…" : "Save invoice settings"}
                </button>
              </div>
            </form>
          </section>
        </>
      )}
    </>
  );
}

function TablePanel({
  headers,
  rows,
  empty,
}: {
  headers: string[];
  rows: ReactNode[][];
  empty: string;
}) {
  const [filter, setFilter] = useState("");
  const filtered = filter.trim()
    ? rows.filter((row) =>
        row.some(
          (cell) =>
            typeof cell === "string" &&
            cell.toLowerCase().includes(filter.trim().toLowerCase()),
        ),
      )
    : rows;
  return (
    <section className="admin-panel table-panel">
      <div className="table-toolbar">
        <div className="table-search">
          <Search />
          <input
            aria-label="Filter records"
            placeholder="Filter records"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </div>
        <span>
          {filtered.length} of {rows.length} records
        </span>
      </div>
      <div className="admin-table-wrap">
        <table>
          <thead>
            <tr>
              {headers.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length ? (
              filtered.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{cell as ReactNode}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headers.length} className="empty-cell">
                  {rows.length ? "No records match this filter." : empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

type ContractLineState = {
  skuId: string;
  revisionKey: string;
  billingState: "billing" | "override" | "informational";
  overridePriceCents: number | null;
  quantity: number;
  startDate: string;
  endDate: string;
  sourceBundleId?: number | null;
};

function ContractLineEditor({
  line,
  index,
  skus,
  managedOnly,
  pricingLocked = false,
  onChange,
  onRemove,
}: {
  line: ContractLineState;
  index: number;
  skus: RecordRow[];
  managedOnly: boolean;
  pricingLocked?: boolean;
  onChange: (line: ContractLineState) => void;
  onRemove: () => void;
}) {
  const revisions = useApi<{ revisions: RecordRow[] }>(
    line.skuId ? `skus/${line.skuId}/revisions` : "",
  );
  const eligible = managedOnly
    ? skus.filter((row) => String(row.service_scope) === "managed")
    : skus;
  return (
    <div className="contract-line-editor">
      <label>
        SKU *
        <select
          value={line.skuId}
          onChange={(event) => {
            const sku = eligible.find((row) => String(row.id) === event.target.value);
            onChange({
              ...line,
              skuId: event.target.value,
              revisionKey: String(sku?.current_revision_key || ""),
            });
          }}
        >
          <option value="">Select SKU</option>
          {eligible.map((row) => (
            <option key={String(row.id)} value={String(row.id)}>
              {String(row.sku)} · {String(row.name)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Backend revision *
        <select
          value={line.revisionKey}
          disabled={!line.skuId || revisions.loading}
          onChange={(event) => onChange({ ...line, revisionKey: event.target.value })}
        >
          <option value="">Select revision</option>
          {(revisions.data?.revisions || []).map((row, revisionIndex) => (
            <option key={String(row.revision_key)} value={String(row.revision_key)}>
              {revisionIndex === 0 ? "Current" : "Prior"} · {String(row.revision_id)} · {currency(row.unit_price_cents)}
            </option>
          ))}
        </select>
      </label>
      <label>
        State
        <select
          value={line.billingState}
          disabled={pricingLocked}
          onChange={(event) => onChange({
            ...line,
            billingState: event.target.value as ContractLineState["billingState"],
            overridePriceCents: event.target.value === "override" ? line.overridePriceCents ?? 0 : null,
          })}
        >
          <option value="billing">Billing</option>
          <option value="override">Override</option>
          <option value="informational">Informational</option>
        </select>
      </label>
      <label>
        Override price
        <input
          type="number"
          min="0"
          step="0.01"
          disabled={pricingLocked || line.billingState !== "override"}
          value={line.overridePriceCents === null ? "" : line.overridePriceCents / 100}
          onChange={(event) => onChange({ ...line, overridePriceCents: Math.round(Number(event.target.value) * 100) })}
        />
      </label>
      <label>
        Quantity
        <input
          type="number"
          min="1"
          step="1"
          value={line.quantity}
          onChange={(event) => onChange({ ...line, quantity: Number(event.target.value) })}
        />
      </label>
      <label>
        Start date
        <input type="date" value={line.startDate} onChange={(event) => onChange({ ...line, startDate: event.target.value })} />
      </label>
      <label>
        End date
        <input type="date" value={line.endDate} onChange={(event) => onChange({ ...line, endDate: event.target.value })} />
      </label>
      <button type="button" className="contract-line-remove" onClick={onRemove} aria-label={`Remove contracted SKU ${index + 1}`}>
        <Trash2 />
      </button>
    </div>
  );
}

function ContractDialog({
  customerId,
  initial,
  onClose,
  onSaved,
}: {
  customerId: string;
  initial?: RecordRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const customers = useApi<{ customers: RecordRow[] }>("customers");
  const skus = useApi<{ skus: RecordRow[] }>("skus");
  const bundles = useApi<{ bundles: RecordRow[] }>("bundles");
  const [serviceType, setServiceType] = useState<"device" | "service">(
    initial?.service_type === "service" ? "service" : "device",
  );
  const today = new Date().toISOString().slice(0, 10);
  const [lines, setLines] = useState<ContractLineState[]>(
    ((initial?.lines as RecordRow[]) || []).map((line) => ({
      skuId: String(line.sku_id),
      revisionKey: String(line.sku_revision_key),
      billingState: String(line.billing_state) as ContractLineState["billingState"],
      overridePriceCents: line.override_price_cents === null ? null : Number(line.override_price_cents),
      quantity: Number(line.quantity || 1),
      startDate: String(line.start_date || today),
      endDate: String(line.end_date || ""),
      sourceBundleId: line.source_bundle_id ? Number(line.source_bundle_id) : null,
    })),
  );
  const [bundleId, setBundleId] = useState(String(initial?.source_bundle_id || ""));
  const [realIp, setRealIp] = useState(String(initial?.real_ip || ""));
  const [natIp, setNatIp] = useState(String(initial?.nat_ip || ""));
  const initialManagementUrl = String(initial?.management_url || "");
  const [managementUrl, setManagementUrl] = useState(
    initialManagementUrl || suggestedManagementUrl(String(initial?.nat_ip || ""), String(initial?.real_ip || "")),
  );
  const [managementUrlOverridden, setManagementUrlOverridden] = useState(Boolean(initialManagementUrl));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const selectedBundle = (bundles.data?.bundles || []).find((row) => String(row.id) === bundleId);
  function updateDeviceIp(kind: "real" | "nat", value: string) {
    const nextRealIp = kind === "real" ? value : realIp;
    const nextNatIp = kind === "nat" ? value : natIp;
    if (kind === "real") setRealIp(value); else setNatIp(value);
    if (!managementUrlOverridden) setManagementUrl(suggestedManagementUrl(nextNatIp, nextRealIp));
  }
  const addLine = () => setLines((value) => [...value, {
    skuId: "", revisionKey: "", billingState: "billing", overridePriceCents: null,
    quantity: 1, startDate: today, endDate: "", sourceBundleId: null,
  }]);
  function chooseBundle(value: string) {
    setBundleId(value);
    const bundle = (bundles.data?.bundles || []).find((row) => String(row.id) === value);
    if (!bundle) return;
    const components = ((bundle.items as RecordRow[]) || []).map((item) => {
      const sku = (skus.data?.skus || []).find((row) => Number(row.id) === Number(item.sku_id));
      return {
        skuId: String(item.sku_id),
        revisionKey: String(sku?.current_revision_key || ""),
        billingState: "billing" as const,
        overridePriceCents: null,
        quantity: Number(item.quantity || 1),
        startDate: today,
        endDate: "",
        sourceBundleId: Number(value),
      };
    });
    if (components.length) setLines(components);
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api(initial ? `contracted-services/contracts/${String(initial.id)}` : "contracted-services/contracts", {
        method: initial ? "PUT" : "POST",
        body: JSON.stringify({
          customerId: form.get("customerId"), serviceType, serviceName: form.get("serviceName"),
          serialNumber: form.get("serialNumber"), hostname: form.get("hostname"), realIp: form.get("realIp"),
          natIp: form.get("natIp"), managementUrl: form.get("managementUrl"), notes: form.get("notes"), sourceBundleId: bundleId || null, lines,
        }),
      });
      onSaved();
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to save contracted service.");
    } finally {
      setSaving(false);
    }
  }
  const loading = customers.loading || skus.loading || bundles.loading;
  return (
    <Dialog title={initial ? "Edit contracted service" : "Add contracted service"} eyebrow="Customer contract" onClose={onClose} className="contract-dialog">
      {loading ? <Loading /> : customers.error || skus.error || bundles.error ? (
        <ErrorNotice message={customers.error || skus.error || bundles.error} />
      ) : (
        <form className="admin-form" onSubmit={submit}>
          <div className="admin-field-grid contract-device-fields">
            <label>
              Customer *
              <select name="customerId" required defaultValue={String(initial?.customer_id || customerId)}>
                <option value="">Select customer</option>
                {customers.data?.customers.map((row) => <option key={String(row.id)} value={String(row.id)}>{String(row.customer_number)} · {String(row.company_name)}</option>)}
              </select>
            </label>
            <label>
              Contract type
              <select value={serviceType} onChange={(event) => { setServiceType(event.target.value as "device" | "service"); setBundleId(""); setLines([]); }}>
                <option value="device">Device</option>
                <option value="service">Service</option>
              </select>
            </label>
            {serviceType === "service" ? (
              <>
                <label>Service name *<input name="serviceName" required maxLength={160} defaultValue={String(initial?.service_name || "")} /></label>
                <label>
                  Copy package components
                  <select value={bundleId} onChange={(event) => chooseBundle(event.target.value)}>
                    <option value="">No package</option>
                    {bundles.data?.bundles.filter((row) => Number(row.active) === 1 || String(row.id) === bundleId)
                      .map((row) => <option key={String(row.id)} value={String(row.id)}>{String(row.name)}{Number(row.active) === 1 ? "" : " (inactive)"}</option>)}
                  </select>
                  <small>{selectedBundle
                    ? selectedBundle.pricing_mode === "fixed"
                      ? `Fixed package billing: ${currency(initial?.source_bundle_id === selectedBundle.id ? initial?.package_price_cents : selectedBundle.price_cents)}. SKU prices are not billed separately.`
                      : `Calculated package billing: ${currency(selectedBundle.calculated_price_cents)} from the included SKU quantities.`
                    : "Choose a package to copy its components and billing method."}</small>
                </label>
              </>
            ) : (
              <>
                <label>Serial number *<input name="serialNumber" required maxLength={160} defaultValue={String(initial?.serial_number || "")} /></label>
                <label>Hostname<input name="hostname" maxLength={160} defaultValue={String(initial?.hostname || "")} /></label>
                <label>Real IP<input name="realIp" maxLength={200} placeholder="192.0.2.10 or CIDR" value={realIp} onChange={(event) => updateDeviceIp("real", event.target.value)} /></label>
                <label>NAT IP<input name="natIp" maxLength={200} placeholder="198.51.100.10 or CIDR" value={natIp} onChange={(event) => updateDeviceIp("nat", event.target.value)} /></label>
                <label className="management-url-field">Management URL<input name="managementUrl" type="url" maxLength={2000} placeholder="https://host:8443" value={managementUrl}
                  onChange={(event) => { setManagementUrl(event.target.value); setManagementUrlOverridden(Boolean(event.target.value.trim())); }}
                  onBlur={() => { if (!managementUrl.trim()) { setManagementUrl(suggestedManagementUrl(natIp, realIp)); setManagementUrlOverridden(false); } }} />
                  <small>Defaults to NAT IP, then Real IP. You can add a port or replace the URL.</small></label>
              </>
            )}
          </div>
          <label>Notes<textarea name="notes" rows={3} maxLength={4000} defaultValue={String(initial?.notes || "")} /></label>
          <div className="contract-lines-heading">
            <div><strong>Contracted SKUs</strong><span>{serviceType === "device" ? "Only Managed SKUs are eligible." : "Package components can be edited independently."}</span></div>
            <button type="button" className="admin-button secondary" onClick={addLine}><Plus /> Add SKU</button>
          </div>
          <div className="contract-lines">
            {lines.map((line, index) => (
              <ContractLineEditor key={index} line={line} index={index} skus={skus.data?.skus || []} managedOnly={serviceType === "device"}
                pricingLocked={serviceType === "service" && Boolean(bundleId)}
                onChange={(updated) => setLines((value) => value.map((item, itemIndex) => itemIndex === index ? updated : item))}
                onRemove={() => setLines((value) => value.filter((_, itemIndex) => itemIndex !== index))} />
            ))}
            {!lines.length && <p className="admin-form-note">Add at least one contracted SKU.</p>}
          </div>
          {error && <p className="admin-form-error">{error}</p>}
          <div className="admin-form-actions">
            <button type="button" className="admin-button secondary" onClick={onClose}>Cancel</button>
            <button className="admin-button primary" disabled={saving || !lines.length}>{saving ? "Saving…" : "Save contracted service"}</button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

function parseContractCsv(text: string) {
  const rows: string[][] = [];
  let current: string[] = [], field = "", quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (quoted && character === '"' && text[index + 1] === '"') { field += '"'; index += 1; }
    else if (character === '"') quoted = !quoted;
    else if (character === "," && !quoted) { current.push(field.trim()); field = ""; }
    else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && text[index + 1] === "\n") index += 1;
      current.push(field.trim()); field = "";
      if (current.some(Boolean)) rows.push(current);
      current = [];
    } else field += character;
  }
  current.push(field.trim());
  if (current.some(Boolean)) rows.push(current);
  if (rows.length < 2) throw new Error("CSV must contain a header and at least one row.");
  const headers = rows[0].map((header) => header.toLowerCase().trim().replace(/\s+/g, "_"));
  for (const required of ["serial_number", "sku"]) if (!headers.includes(required)) throw new Error(`CSV must include ${required}.`);
  const get = (cells: string[], name: string) => cells[headers.indexOf(name)] || "";
  return rows.slice(1).map((cells) => ({
    customerNumber: get(cells, "customer_number"), serialNumber: get(cells, "serial_number"),
    hostname: get(cells, "hostname"), realIp: get(cells, "real_ip"), natIp: get(cells, "nat_ip"),
    managementUrl: get(cells, "management_url"),
    notes: get(cells, "notes"), sku: get(cells, "sku"), revision: get(cells, "revision"),
    state: get(cells, "state") || "billing", overridePrice: get(cells, "override_price"),
    startDate: get(cells, "start_date"), endDate: get(cells, "end_date"),
  }));
}

function ContractImportDialog({ customerId, onClose, onSaved }: { customerId: string; onClose: () => void; onSaved: () => void }) {
  const [rows, setRows] = useState<RecordRow[]>([]);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState<RecordRow | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  function downloadTemplate() {
    const content = "customer_number,serial_number,hostname,real_ip,nat_ip,management_url,notes,sku,revision,state,override_price,start_date,end_date\nCUS-000001,FG100FTK12345678,main-fw,192.0.2.10,198.51.100.10,,Primary firewall,MFW-001,,billing,,2026-08-01,\n";
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([content], { type: "text/csv" }));
    link.download = "contracted-device-import-template.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  }
  async function chooseFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setPreview(null); setError("");
    if (!file) return;
    try { setRows(parseContractCsv(await file.text())); setFileName(file.name); }
    catch (reason) { setRows([]); setError(reason instanceof Error ? reason.message : "Unable to read CSV."); }
  }
  async function run(previewOnly: boolean) {
    setBusy(true); setError("");
    try {
      const result = await api<RecordRow>(previewOnly ? "contracted-services/import/preview" : "contracted-services/import", {
        method: "POST", body: JSON.stringify({ customerId: customerId || null, fileName, rows }),
      });
      setPreview(result);
      if (!previewOnly) { onSaved(); onClose(); }
    } catch (reason) { setPreview(null); setError(reason instanceof Error ? reason.message : "Unable to import CSV."); }
    finally { setBusy(false); }
  }
  return (
    <Dialog title="Import contracted devices" eyebrow="Validated CSV import" onClose={onClose}>
      <div className="admin-form">
        <p className="admin-form-note">Preview is required. Blank revision uses the current SKU revision; blank state uses Billing; blank start date uses today; blank end date is ongoing. Dates may use YYYY-MM-DD or M/D/YYYY. Unknown customers, SKUs, revisions, invalid IPs, and cross-customer serial conflicts are rejected before any write.</p>
        <label>CSV file *<input type="file" accept=".csv,text/csv" onChange={chooseFile} /></label>
        {rows.length > 0 && <p className="admin-form-success">Loaded {rows.length} rows from {fileName}.</p>}
        {preview && <div className="import-preview"><strong>Validated preview</strong><span>{String(preview.createCount)} devices create</span><span>{String(preview.updateCount)} rows update</span><span>{String(preview.unchangedCount)} unchanged</span></div>}
        {error && <p className="admin-form-error">{error}</p>}
        <div className="admin-form-actions">
          <button type="button" className="admin-button secondary" onClick={downloadTemplate}><Download /> Template</button>
          <button type="button" className="admin-button secondary" disabled={busy || !rows.length} onClick={() => run(true)}>Preview import</button>
          <button type="button" className="admin-button primary" disabled={busy || !preview} onClick={() => run(false)}><Upload /> Confirm import</button>
        </div>
      </div>
    </Dialog>
  );
}

function ZabbixProvisionDialog({
  contract,
  onClose,
  onProvisioned,
}: {
  contract: RecordRow;
  onClose: () => void;
  onProvisioned: () => void;
}) {
  const [version, setVersion] = useState<"2" | "3">("2");
  const [securityLevel, setSecurityLevel] = useState("authPriv");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ message: string; zabbix_id: string } | null>(null);
  const realIp = monitoringIp(contract.real_ip);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const response = await fetch("/api/provision-node", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contract_id: contract.id,
          customer_name: contract.company_name,
          serial_number: contract.serial_number,
          node_ip: realIp,
          snmp_version: Number(version),
          snmp_port: Number(form.get("snmpPort")),
          snmp_community: form.get("snmpCommunity"),
          snmp_security_name: form.get("snmpSecurityName"),
          snmp_context_name: form.get("snmpContextName"),
          snmp_security_level: securityLevel,
          snmp_auth_protocol: form.get("snmpAuthProtocol"),
          snmp_auth_passphrase: form.get("snmpAuthPassphrase"),
          snmp_privacy_protocol: form.get("snmpPrivacyProtocol"),
          snmp_privacy_passphrase: form.get("snmpPrivacyPassphrase"),
        }),
      });
      const body = await response.json().catch(() => ({})) as { message?: string; zabbix_id?: string };
      if (!response.ok) throw new Error(body.message || "Zabbix provisioning failed.");
      setResult({ message: body.message || "Device provisioned in Zabbix.", zabbix_id: String(body.zabbix_id || "") });
      onProvisioned();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Zabbix provisioning failed.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog title="Provision Zabbix monitoring" eyebrow="Contracted device" onClose={onClose}>
      {result ? (
        <div className="admin-form zabbix-result">
          <CheckCircle2 />
          <h3>{result.message}</h3>
          <p>Zabbix host ID <code>{result.zabbix_id}</code></p>
          <div className="admin-form-actions"><button className="admin-button primary" onClick={onClose}>Done</button></div>
        </div>
      ) : (
        <form className="admin-form" onSubmit={submit}>
          <p className="admin-form-note">Creates the tenant host group when needed and provisions this device through its contracted Real IP. Credentials are sent directly to Zabbix as secret host macros and are not stored in this database.</p>
          <div className="admin-field-grid">
            <label>Customer<input value={String(contract.company_name || "")} disabled /></label>
            <label>Serial number<input value={String(contract.serial_number || "")} disabled /></label>
            <label>Zabbix node name<input value={String(contract.hostname || "")} disabled /></label>
            <label>Real IP<input value={realIp} disabled /></label>
            <label>SNMP port<input name="snmpPort" type="number" min="1" max="65535" defaultValue="161" required /></label>
            <label>SNMP version<select value={version} onChange={(event) => setVersion(event.target.value as "2" | "3")}><option value="2">SNMPv2c</option><option value="3">SNMPv3</option></select></label>
            {version === "2" ? (
              <label>Community *<input name="snmpCommunity" type="password" autoComplete="new-password" maxLength={255} required /></label>
            ) : <>
              <label>Security name *<input name="snmpSecurityName" maxLength={255} required /></label>
              <label>Context name<input name="snmpContextName" maxLength={255} /></label>
              <label>Security level<select value={securityLevel} onChange={(event) => setSecurityLevel(event.target.value)}><option value="noAuthNoPriv">noAuthNoPriv</option><option value="authNoPriv">authNoPriv</option><option value="authPriv">authPriv</option></select></label>
              {securityLevel !== "noAuthNoPriv" && <>
                <label>Authentication protocol<select name="snmpAuthProtocol" defaultValue="SHA256"><option>SHA256</option><option>SHA384</option><option>SHA512</option><option>SHA224</option><option>SHA1</option><option>MD5</option></select></label>
                <label>Authentication passphrase *<input name="snmpAuthPassphrase" type="password" autoComplete="new-password" minLength={8} maxLength={255} required /></label>
              </>}
              {securityLevel === "authPriv" && <>
                <label>Privacy protocol<select name="snmpPrivacyProtocol" defaultValue="AES128"><option>AES128</option><option>AES192</option><option>AES256</option><option>AES192C</option><option>AES256C</option><option>DES</option></select></label>
                <label>Privacy passphrase *<input name="snmpPrivacyPassphrase" type="password" autoComplete="new-password" minLength={8} maxLength={255} required /></label>
              </>}
            </>}
          </div>
          {error && <p className="admin-form-error">{error}</p>}
          <div className="admin-form-actions">
            <button type="button" className="admin-button secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="admin-button primary" disabled={busy || !realIp || !String(contract.hostname || "").trim()}>{busy ? <><LoaderCircle className="spin" /> Provisioning…</> : <><Activity /> Provision in Zabbix</>}</button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

function ContractedServicesPage({
  refresh,
  onAdd,
  onEdit,
  onImport,
  onChanged,
}: {
  refresh: number;
  onAdd: (customerId: string) => void;
  onEdit: (customerId: string, contract: RecordRow) => void;
  onImport: (customerId: string) => void;
  onChanged: () => void;
}) {
  const customers = useApi<{ customers: RecordRow[] }>("contracted-services/customers", refresh);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const contracts = useApi<{ contracts: RecordRow[] }>(selectedCustomerId ? `contracted-services/contracts?customerId=${encodeURIComponent(selectedCustomerId)}` : "", refresh);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [previews, setPreviews] = useState<RecordRow[]>([]);
  const [selectedBillingCustomer, setSelectedBillingCustomer] = useState("");
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [provisioningContract, setProvisioningContract] = useState<RecordRow | null>(null);
  async function preview() {
    setBusy("preview"); setError(""); setMessage("");
    try {
      const result = await api<{ previews: RecordRow[] }>("contracted-services/invoices/preview", { method: "POST", body: JSON.stringify({ period }) });
      setPreviews(result.previews); setSelectedBillingCustomer(result.previews[0] ? String(result.previews[0].customerId) : "");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to preview invoices."); }
    finally { setBusy(""); }
  }
  async function generate(customerId = "") {
    if (!window.confirm(`Generate ${customerId ? "this customer's" : "all eligible"} ${period} invoices as drafts?`)) return;
    setBusy("generate"); setError("");
    try {
      const result = await api<{ message: string }>("contracted-services/invoices/generate", { method: "POST", body: JSON.stringify({ period, customerId }) });
      setMessage(result.message); setPreviews([]); onChanged();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to generate invoices."); }
    finally { setBusy(""); }
  }
  async function remove(contract: RecordRow) {
    if (!window.confirm("Delete this unused contract, or archive it if invoice history exists?")) return;
    try { await api(`contracted-services/contracts/${String(contract.id)}`, { method: "DELETE" }); onChanged(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to remove contract."); }
  }
  return (
    <>
      {provisioningContract && <ZabbixProvisionDialog contract={provisioningContract} onClose={() => setProvisioningContract(null)} onProvisioned={onChanged} />}
      <PageHeader title="Contracted Services" copy="Customer devices and services that produce controlled monthly billing snapshots."
        action={<div className="heading-actions"><button className="admin-button secondary" onClick={() => onImport(selectedCustomerId)}><Upload /> Import devices</button><button className="admin-button primary" onClick={() => onAdd(selectedCustomerId)}><Plus /> Add contract</button></div>} />
      <section className="admin-panel billing-run-panel">
        <div>
          <label>Billing month<input type="month" value={period} onChange={(event) => { setPeriod(event.target.value); setPreviews([]); }} /></label>
          <button className="admin-button secondary" disabled={Boolean(busy)} onClick={preview}>{busy === "preview" ? "Building preview…" : "Preview eligible customers"}</button>
          {previews.length > 0 && <>
            <label>Eligible customer<select value={selectedBillingCustomer} onChange={(event) => setSelectedBillingCustomer(event.target.value)}>{previews.map((row) => <option key={String(row.customerId)} value={String(row.customerId)}>{String(row.customerNumber)} · {String(row.companyName)}</option>)}</select></label>
            <button className="admin-button primary" disabled={Boolean(busy)} onClick={() => generate(selectedBillingCustomer)}>Generate customer</button>
            <button className="admin-button primary" disabled={Boolean(busy)} onClick={() => generate()}>Generate all eligible</button>
          </>}
        </div>
        <p>Issue date is the first day of the selected month. Informational-only customers are excluded. Annual lines bill in their anniversary month; one-time lines bill in their start month.</p>
      </section>
      {message && <p className="admin-form-success page-error">{message}</p>}
      {error && <p className="admin-form-error page-error">{error}</p>}
      {previews.length > 0 && <section className="preview-grid">{previews.map((row) => <article className="admin-panel preview-card" key={String(row.customerId)}><header><div><h3>{String(row.companyName)}</h3><span>{String(row.customerNumber)} · {Array.isArray(row.lines) ? row.lines.length : 0} lines</span></div><Status value={row.canGenerate ? "ready" : "already generated"} /></header><div>{((row.lines as RecordRow[]) || []).map((line) => <span key={String(line.contracted_service_line_id)}><strong>{line.pricingSource === "fixed_package" ? String(line.serviceIdentity) : `${String(line.sku)} · ${String(line.serviceIdentity)}`}</strong><small>{line.pricingSource === "fixed_package" ? "Fixed package" : titleCase(line.pricingSource)} · {currency(line.lineTotalCents)}</small></span>)}</div><footer><span>Tax {currency(row.taxCents)}</span><strong>{currency(row.totalCents)}</strong></footer></article>)}</section>}
      {customers.loading ? <Loading /> : customers.error ? <ErrorNotice message={customers.error} /> : (
        <div className="contracted-layout">
          <section className="admin-panel contract-customer-list">
            <h3>Customers</h3>
            {(customers.data?.customers || []).map((row) => <button key={String(row.id)} className={selectedCustomerId === String(row.id) ? "active" : ""} onClick={() => setSelectedCustomerId(String(row.id))}><span><strong>{String(row.company_name)}</strong><small>{String(row.customer_number)}</small></span><em>{String(row.contract_count)}</em></button>)}
          </section>
          <section>
            {!selectedCustomerId ? <div className="admin-panel empty-cell">Select a customer to manage contracted devices and services.</div> : contracts.loading ? <Loading /> : contracts.error ? <ErrorNotice message={contracts.error} /> : (
              <div className="contract-card-grid">{(contracts.data?.contracts || []).map((contract) => {
                const isDevice = String(contract.service_type) === "device";
                const hostname = String(contract.hostname || "").trim();
                const managementUrl = String(contract.management_url || suggestedManagementUrl(String(contract.nat_ip || ""), String(contract.real_ip || "")));
                return <article className="admin-panel contract-card" key={String(contract.id)}>
                  <header><div><Status value={contract.service_type} /><h3>{isDevice ? String(contract.hostname || contract.serial_number) : String(contract.service_name)}</h3><span>{String(contract.serial_number || "Service contract")}</span></div><div className="row-actions"><button onClick={() => onEdit(selectedCustomerId, contract)} title="Edit"><Pencil /></button><button className="danger" onClick={() => remove(contract)} title="Delete or archive"><Trash2 /></button></div></header>
                  {isDevice && <><p>{String(contract.real_ip || "No Real IP")} · NAT {String(contract.nat_ip || "—")}</p><div className="device-access-actions">
                    {managementUrl && <a href={managementUrl} target="_blank" rel="noreferrer" title={managementUrl}><ExternalLink /> Management</a>}
                    {hostname && <a href={`ssh://${encodeURI(hostname)}`} title={`Open SSH to ${hostname}`}><Terminal /> SSH</a>}
                    {contract.zabbix_host_id ? <span className="zabbix-provisioned"><CheckCircle2 /> Zabbix {String(contract.zabbix_host_id)}</span> : <button disabled={!monitoringIp(contract.real_ip) || !String(contract.hostname || "").trim()} onClick={() => setProvisioningContract(contract)} title={!String(contract.hostname || "").trim() ? "Add a hostname before provisioning" : monitoringIp(contract.real_ip) ? "Provision SNMP monitoring in Zabbix" : "Add a Real IP before provisioning"}><Activity /> {contract.monitoring_status === "failed" ? "Retry Zabbix" : "Monitor in Zabbix"}</button>}
                  </div></>}
                  {isDevice && contract.monitoring_status === "failed" && <p className="zabbix-error">{String(contract.zabbix_last_error || "The last provisioning attempt failed.")}</p>}
                  {!isDevice && Boolean(contract.package_pricing_mode) && <p>{contract.package_pricing_mode === "fixed"
                    ? `Fixed package · ${currency(contract.package_price_cents)} ${titleCase(contract.package_billing_period)}`
                    : "Calculated from included SKUs"}</p>}
                  <div>{((contract.lines as RecordRow[]) || []).map((line) => <span key={String(line.id)}><strong>{String(line.sku)} · {String(line.sku_name)}</strong><small>{contract.package_pricing_mode === "fixed"
                    ? `Included component · ${shortDate(line.start_date)} to ${line.end_date ? shortDate(line.end_date) : "ongoing"}`
                    : `${titleCase(line.billing_state)} · ${line.billing_state === "informational" ? currency(0) : line.billing_state === "override" ? currency(line.override_price_cents) : currency(line.unit_price_cents)} · ${shortDate(line.start_date)} to ${line.end_date ? shortDate(line.end_date) : "ongoing"}`}</small></span>)}</div>
                </article>;
              })}</div>
            )}
          </section>
        </div>
      )}
    </>
  );
}

export function AdminPortal() {
  const [dialog, setDialog] = useState<{
    type:
      | "invoice"
      | "customer"
      | "sku"
      | "sku-clone"
      | "sku-usage"
      | "sku-history"
      | "sku-templates"
      | "sku-apply-template"
      | "package"
      | "package-clone"
      | "payment"
      | "categories"
      | "contract"
      | "contract-import";
    record?: RecordRow;
  } | null>(null);
  const [refresh, setRefresh] = useState(0);
  const saved = () => setRefresh((value) => value + 1);
  return (
    <AdminShell onNewInvoice={() => setDialog({ type: "invoice" })}>
      <Routes>
        <Route
          index
          element={
            <Dashboard
              onNewInvoice={() => setDialog({ type: "invoice" })}
              onAddCustomer={() => setDialog({ type: "customer" })}
              onAddSku={() => setDialog({ type: "sku" })}
            />
          }
        />
        <Route
          path="customers"
          element={
            <CustomersPage
              refresh={refresh}
              onAdd={() => setDialog({ type: "customer" })}
              onEdit={(record) => setDialog({ type: "customer", record })}
            />
          }
        />
        <Route
          path="invoices"
          element={
            <InvoicesPage
              refresh={refresh}
              onAdd={() => setDialog({ type: "invoice" })}
              onEdit={(record) => setDialog({ type: "invoice", record })}
              onChanged={saved}
            />
          }
        />
        <Route
          path="contracted-services"
          element={
            <ContractedServicesPage
              refresh={refresh}
              onAdd={(customerId) => setDialog({ type: "contract", record: { customer_id: customerId } })}
              onImport={(customerId) => setDialog({ type: "contract-import", record: { customer_id: customerId } })}
              onEdit={(_customerId, record) => setDialog({ type: "contract", record })}
              onChanged={saved}
            />
          }
        />
        <Route
          path="catalog"
          element={
            <CatalogPage
              refresh={refresh}
              onAdd={() => setDialog({ type: "sku" })}
              onEdit={(record) => setDialog({ type: "sku", record })}
              onClone={(record) => setDialog({ type: "sku-clone", record })}
              onUsage={(record) => setDialog({ type: "sku-usage", record })}
              onHistory={(record) => setDialog({ type: "sku-history", record })}
              onCategories={() => setDialog({ type: "categories" })}
              onTemplates={() => setDialog({ type: "sku-templates" })}
              onApplyTemplate={(skus) => setDialog({ type: "sku-apply-template", record: { skus } })}
              onChanged={saved}
            />
          }
        />
        <Route
          path="packages"
          element={
            <PackagesPage
              refresh={refresh}
              onAdd={() => setDialog({ type: "package" })}
              onEdit={(record) => setDialog({ type: "package", record })}
              onClone={(record) => setDialog({ type: "package-clone", record })}
              onChanged={saved}
            />
          }
        />
        <Route
          path="payments"
          element={
            <PaymentsPage
              refresh={refresh}
              onAdd={() => setDialog({ type: "payment" })}
            />
          }
        />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="ssh-setup" element={<PuttySetupPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Routes>
      {dialog?.type === "customer" && (
        <CustomerDialog
          initial={dialog.record}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}
      {dialog?.type === "sku" && (
        <SkuDialog
          initial={dialog.record}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}
      {dialog?.type === "sku-clone" && dialog.record && (
        <SkuDialog
          initial={dialog.record}
          cloning
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}
      {dialog?.type === "sku-usage" && dialog.record && (
        <SkuUsageDialog sku={dialog.record} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "sku-templates" && (
        <SkuTemplateManagerDialog onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "sku-apply-template" && Array.isArray(dialog.record?.skus) && (
        <ApplySkuTemplateDialog skus={dialog.record.skus as RecordRow[]} onClose={() => setDialog(null)} onSaved={saved} />
      )}
      {dialog?.type === "sku-history" && dialog.record && (
        <SkuHistoryDialog sku={dialog.record} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "contract" && (
        <ContractDialog
          customerId={String(dialog.record?.customer_id || "")}
          initial={dialog.record?.id ? dialog.record : undefined}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}
      {dialog?.type === "contract-import" && (
        <ContractImportDialog customerId={String(dialog.record?.customer_id || "")} onClose={() => setDialog(null)} onSaved={saved} />
      )}
      {dialog?.type === "invoice" && (
        <InvoiceDialog
          initial={dialog.record}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}
      {dialog?.type === "package" && (
        <PackageDialog
          initial={dialog.record}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}
      {dialog?.type === "package-clone" && (
        <PackageDialog
          initial={dialog.record}
          cloning
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}
      {dialog?.type === "payment" && (
        <PaymentDialog onClose={() => setDialog(null)} onSaved={saved} />
      )}
      {dialog?.type === "categories" && (
        <CategoryManagerDialog
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}
    </AdminShell>
  );
}
